
// @ts-nocheck
import React, { useState, useRef, useEffect } from "react";
import { api } from "@/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Send, X, Minimize2, Maximize2, User, Loader2, CheckCircle, AlertCircle, Plus, FileText, Users as UsersIcon, Mail, MessageSquare, ClipboardList, TrendingUp, Check, Target, TrendingDown, Zap, AlertTriangle, Star, HelpCircle, ListChecks, Scale, Type, CheckSquare, Circle, BarChart3, Brain, Megaphone, Calendar as CalendarIcon, Folder, Paperclip, Upload, GitBranch, ChevronDown
} from "lucide-react";
import OliAiIcon from "../OliAiIcon";
import { useTimezone } from '@/components/utils/useTimezone';
import { nowInTimezone, formatTime } from '@/components/utils/timezone';
import { motion } from "framer-motion";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import WorkflowExecutor from "../workflows/WorkflowExecutor";
import { DatePicker } from "@/components/common";
import { useOliConfig } from "@/lib/oli";

// Component to render formatted messages
function FormattedMessage({ content }) {
  let cleanContent = content
    .replace(/\*\*\*/g, '')
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .replace(/###/g, '')
    .replace(/##/g, '')
    .replace(/(?:^|\n)#\s+/g, '\n');

  const lines = cleanContent.split('\n');
  const elements = [];
  let currentList = null;
  let listItems = [];

  const flushList = () => {
    if (currentList && listItems.length > 0) {
      elements.push(
        <ul key={elements.length} className="space-y-1.5 my-3 ml-1">
          {listItems.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span className="text-indigo-600 mt-0.5 font-bold">•</span>
              <span className="flex-1 text-slate-700">{item}</span>
            </li>
          ))}
        </ul>
      );
      listItems = [];
      currentList = null;
    }
  };

  lines.forEach((line, idx) => {
    const trimmedLine = line.trim();
    
    if (!trimmedLine) {
      flushList();
      elements.push(<div key={`space-${idx}`} className="h-1" />);
      return;
    }

    const isHeader = trimmedLine.endsWith(':') || 
                     (trimmedLine.length < 50 && /^[A-Z]/.test(trimmedLine) && !trimmedLine.match(/^[•\-\*\d]/));

    if (trimmedLine.match(/^[•\-\*]\s+/) || trimmedLine.match(/^\d+\.\s+/)) {
      const text = trimmedLine.replace(/^[•\-\*]\s+/, '').replace(/^\d+\.\s+/, '');
      currentList = true;
      listItems.push(text);
    } else if (isHeader) {
      flushList();
      const text = trimmedLine.replace(/:$/, '');
      elements.push(
        <h4 key={idx} className="font-bold text-slate-900 mt-4 mb-2 text-sm">
          {text}
        </h4>
      );
    } else {
      flushList();
      elements.push(
        <p key={idx} className="my-1.5 leading-relaxed text-slate-700">
          {trimmedLine}
        </p>
      );
    }
  });

  flushList();

  return <div className="space-y-0.5">{elements}</div>;
}

// Component to display questionnaire preview
function QuestionnairePreview({ questionnaire }) {
  const questionTypeIcons = {
    scale: Scale,
    yes_no: CheckCircle,
    single_choice: Circle,
    multiple_choice: CheckSquare,
    text: Type,
    long_text: FileText
  };

  const pillarColors = {
    planning: 'bg-blue-100 text-blue-700 border-blue-200',
    investing: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    protection: 'bg-purple-100 text-purple-700 border-purple-200',
    tax: 'bg-amber-100 text-amber-700 border-amber-200',
    retirement: 'bg-indigo-100 text-indigo-700 border-indigo-200'
  };

  return (
    <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
      <CardHeader className="border-b border-indigo-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-indigo-900">{questionnaire.title}</CardTitle>
            <p className="text-sm text-indigo-700 mt-1">{questionnaire.description}</p>
          </div>
          <Badge variant="outline" className="bg-indigo-100 text-indigo-700 border-indigo-300">
            {questionnaire.questions?.length || 0} Questions
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        <div className="space-y-2">
          {questionnaire.questions?.slice(0, 5).map((q, idx) => {
            const Icon = questionTypeIcons[q.question_type] || HelpCircle;
            return (
              <div key={idx} className="p-3 bg-white rounded-lg border border-slate-200">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-indigo-700 font-semibold text-sm">{idx + 1}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">{q.question_text}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className={pillarColors[q.pillar]}>
                        {q.pillar}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        <Icon className="w-3 h-3 mr-1" />
                        {q.question_type.replace('_', ' ')}
                      </Badge>
                      {q.max_score && (
                        <Badge variant="outline" className="bg-slate-100 text-slate-700 text-xs">
                          {q.max_score} pts
                        </Badge>
                      )}
                    </div>
                    {q.options && q.options.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-slate-500 mb-1">Options:</p>
                        <div className="flex flex-wrap gap-1">
                          {q.options.slice(0, 3).map((opt, i) => (
                            <Badge key={i} variant="outline" className="text-xs bg-slate-50">
                              {opt}
                            </Badge>
                          ))}
                          {q.options.length > 3 && (
                            <Badge variant="outline" className="text-xs bg-slate-50">
                              +{q.options.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {questionnaire.questions?.length > 5 && (
            <p className="text-xs text-center text-slate-500 italic">
              ... and {questionnaire.questions.length - 5} more questions
            </p>
          )}
        </div>

        {questionnaire.thresholds && (
          <div className="pt-3 border-t border-indigo-200">
            <p className="text-sm font-semibold text-indigo-900 mb-2">Scoring Thresholds:</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {Object.entries(questionnaire.thresholds).map(([pillar, values]) => (
                <div key={pillar} className="p-2 bg-white rounded border border-slate-200">
                  <p className="font-semibold text-slate-700 capitalize mb-1">{pillar}:</p>
                  <div className="space-y-0.5 text-slate-600">
                    <div>• Excellent: ≥{values.excellent}%</div>
                    <div>• Good: ≥{values.good}%</div>
                    <div>• Needs Work: &lt;{values.needs_improvement}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Component to display client segments
function ClientSegments({ segments }) {
  const segmentIcons = {
    'high-value': Star,
    'at-risk': AlertTriangle,
    'high-potential': TrendingUp,
    'disengaged': TrendingDown,
    'active-engaged': Zap,
    'new-clients': OliAiIcon
  };

  const segmentColors = {
    'high-value': 'from-amber-500 to-orange-500',
    'at-risk': 'from-red-500 to-pink-500',
    'high-potential': 'from-emerald-500 to-green-500',
    'disengaged': 'from-slate-500 to-gray-500',
    'active-engaged': 'from-blue-500 to-indigo-500',
    'new-clients': 'from-purple-500 to-pink-500'
  };

  return (
    <div className="space-y-4 mt-3">
      {segments.map((segment, idx) => {
        const Icon = segmentIcons[segment.id] || Target;
        const colorClass = segmentColors[segment.id] || 'from-blue-500 to-indigo-500';
        
        return (
          <Card key={idx} className="border-2 border-slate-200 hover:shadow-lg transition-all">
            <CardHeader className={`bg-gradient-to-r ${colorClass} text-white p-4`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-white text-lg">{segment.name}</CardTitle>
                  <p className="text-white/90 text-sm">{segment.client_count} clients</p>
                </div>
                <Badge variant="outline" className="bg-white/20 text-white border-white/30">
                  {segment.percentage}%
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-1">Description:</p>
                <p className="text-sm text-slate-600">{segment.description}</p>
              </div>
              
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-2">Key Characteristics:</p>
                <ul className="space-y-1">
                  {segment.characteristics.map((char, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-indigo-600 mt-0.5">•</span>
                      <span>{char}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {segment.sample_clients && segment.sample_clients.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">Sample Clients:</p>
                  <div className="flex flex-wrap gap-2">
                    {segment.sample_clients.map((client, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {client}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-2">Recommended Strategies:</p>
                <ul className="space-y-1">
                  {segment.strategies.map((strategy, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-emerald-600 mt-0.5">→</span>
                      <span>{strategy}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// Add new CampaignPreview component
function CampaignPreview({ campaign }) {
  return (
    <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
      <CardHeader className="border-b border-purple-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
            <Megaphone className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-purple-900">{campaign.campaign_name}</CardTitle>
            <p className="text-sm text-purple-700 mt-1">{campaign.campaign_description}</p>
          </div>
          <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
            {campaign.messages?.length || 0} Messages
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {/* Campaign Goal */}
        <div className="p-3 bg-white rounded-lg border border-purple-200">
          <p className="text-xs font-semibold text-purple-900 mb-1">Campaign Goal:</p>
          <p className="text-sm text-slate-700">{campaign.goal}</p>
        </div>

        {/* Target Audience */}
        <div className="p-3 bg-white rounded-lg border border-purple-200">
          <p className="text-xs font-semibold text-purple-900 mb-1">Target Audience:</p>
          <p className="text-sm text-slate-700 mb-2">{campaign.target_audience?.description}</p>
          <Badge className="bg-emerald-100 text-emerald-700">
            ~{campaign.target_audience?.estimated_count} clients
          </Badge>
        </div>

        {/* Messages Preview */}
        <div>
          <p className="text-xs font-semibold text-purple-900 mb-2">Message Sequence:</p>
          <div className="space-y-2">
            {campaign.messages?.slice(0, 3).map((msg, idx) => (
              <div key={idx} className="p-2 bg-white rounded border border-purple-200">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700">
                    Day {msg.delay_days}
                  </Badge>
                  <span className="text-xs font-medium text-slate-700">{msg.name}</span>
                </div>
                {msg.subject && (
                  <p className="text-xs text-slate-600 truncate">{msg.subject}</p>
                )}
              </div>
            ))}
            {campaign.messages?.length > 3 && (
              <p className="text-xs text-center text-slate-500 italic">
                ... and {campaign.messages.length - 3} more messages
              </p>
            )}
          </div>
        </div>

        {/* Success Metrics */}
        {campaign.success_metrics && (
          <div className="p-3 bg-white rounded-lg border border-purple-200">
            <p className="text-xs font-semibold text-purple-900 mb-2">Expected Results:</p>
            <div className="space-y-1 text-xs text-slate-700">
              <div className="flex justify-between">
                <span>Open Rate:</span>
                <span className="font-semibold">{campaign.success_metrics.expected_open_rate}%</span>
              </div>
              <div className="flex justify-between">
                <span>Engagement:</span>
                <span className="font-semibold">{campaign.success_metrics.expected_engagement_rate}%</span>
              </div>
            </div>
          </div>
        )}

        {/* A/B Test Suggestions */}
        {campaign.ab_test_suggestions && campaign.ab_test_suggestions.length > 0 && (
          <div className="pt-3 border-t border-purple-200">
            <p className="text-xs font-semibold text-purple-900 mb-2">A/B Testing Ideas:</p>
            <div className="space-y-1">
              {campaign.ab_test_suggestions.slice(0, 2).map((test, idx) => (
                <div key={idx} className="text-xs text-slate-600">
                  • Test {test.element.replace('_', ' ')}: {test.test_hypothesis}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Extract prefill data for appointment form from the user's raw message
function extractAppointmentPrefill(userInput: string, clients: any[]) {
  const lower = userInput.toLowerCase();
  const prefill: Record<string, any> = {};

  // Client name — "with [Name]" pattern
  const withMatch = userInput.match(/\bwith\s+([A-Za-z][a-z]+(?:\s+[A-Za-z][a-z]+)*)/i);
  if (withMatch) {
    const name = withMatch[1].trim();
    const nameWords = name.toLowerCase().split(/\s+/).filter(w => w.length > 1);
    const isMultiWord = nameWords.length > 1;
    const matched = clients.find(c => {
      const cname = c.full_name.toLowerCase();
      if (isMultiWord) return nameWords.every(w => cname.includes(w));
      return cname.includes(name.toLowerCase()) || name.toLowerCase().includes(cname);
    });
    prefill.client_name = matched ? matched.full_name : name;
  }

  // Auto-generate title from client name
  if (prefill.client_name) {
    prefill.title = `Meeting with ${prefill.client_name}`;
  }

  // Meeting type
  if (/video|zoom|teams|google meet/i.test(lower)) prefill.meeting_type = 'video_call';
  else if (/phone|call/i.test(lower)) prefill.meeting_type = 'phone_call';
  else if (/in.person|in person|office|onsite/i.test(lower)) prefill.meeting_type = 'in_person';

  // Date parsing
  const today = new Date();
  const toISO = (d: Date) => d.toISOString().split('T')[0];
  if (/\btoday\b/.test(lower)) {
    prefill.date = toISO(today);
  } else if (/\btomorrow\b/.test(lower)) {
    const t = new Date(today); t.setDate(t.getDate() + 1); prefill.date = toISO(t);
  } else {
    const days = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
    const dayMatch = lower.match(/\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/);
    if (dayMatch) {
      const target = days.indexOf(dayMatch[1]);
      const current = today.getDay();
      let diff = target - current;
      if (diff <= 0) diff += 7;
      const d = new Date(today); d.setDate(today.getDate() + diff);
      prefill.date = toISO(d);
    } else {
      const monthNames = ['january','february','march','april','may','june','july','august','september','october','november','december'];
      const mMatch = lower.match(/(january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{1,2})/);
      if (mMatch) {
        const m = monthNames.indexOf(mMatch[1]);
        const day = parseInt(mMatch[2]);
        if (m !== -1 && day > 0) {
          prefill.date = `${today.getFullYear()}-${String(m+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
        }
      }
    }
  }

  // Time — "at 3pm", "at 10:30am", "at 14:00"
  const timeMatch = lower.match(/\bat\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/);
  if (timeMatch) {
    let h = parseInt(timeMatch[1]);
    const min = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
    const period = timeMatch[3];
    if (period === 'pm' && h !== 12) h += 12;
    if (period === 'am' && h === 12) h = 0;
    if (h >= 0 && h <= 23) {
      prefill.start_time = `${String(h).padStart(2,'0')}:${String(min).padStart(2,'0')}`;
    }
  }

  return prefill;
}

// Extract prefill data for task form from the user's raw message
function extractTaskPrefill(userInput: string, clients: any[]) {
  const lower = userInput.toLowerCase();
  const prefill: Record<string, any> = {};

  // Client name — "for/with [Name]" pattern
  const clientMatch = userInput.match(/\b(?:for|with)\s+([A-Za-z][a-z]+(?:\s+[A-Za-z][a-z]+)*)/i);
  if (clientMatch) {
    const name = clientMatch[1].trim();
    const nameWords = name.toLowerCase().split(/\s+/).filter(w => w.length > 1);
    const isMultiWord = nameWords.length > 1;
    const matched = clients.find(c => {
      const cname = c.full_name.toLowerCase();
      if (isMultiWord) return nameWords.every(w => cname.includes(w));
      return cname.includes(name.toLowerCase()) || name.toLowerCase().includes(cname);
    });
    prefill.client_name = matched ? matched.full_name : name;
  }

  // Priority
  if (/urgent|asap|immediately|high priority/i.test(lower)) prefill.priority = 'high';
  else if (/low priority|whenever|no rush/i.test(lower)) prefill.priority = 'low';

  // Due date
  const today = new Date();
  const toISO = (d: Date) => d.toISOString().split('T')[0];
  if (/\btoday\b/.test(lower)) {
    prefill.due_date = toISO(today);
  } else if (/\btomorrow\b/.test(lower)) {
    const t = new Date(today); t.setDate(t.getDate() + 1); prefill.due_date = toISO(t);
  } else if (/\bnext week\b/.test(lower)) {
    const t = new Date(today); t.setDate(t.getDate() + 7); prefill.due_date = toISO(t);
  }

  // Category inference
  if (/follow.?up/i.test(lower)) prefill.category = 'follow_up';
  else if (/\breview\b/i.test(lower)) prefill.category = 'review';
  else if (/\bmeeting\b|\bcall\b/i.test(lower)) prefill.category = 'meeting';
  else if (/document|report|form|file/i.test(lower)) prefill.category = 'document';
  else if (/compliance|regulation|audit/i.test(lower)) prefill.category = 'compliance';

  return prefill;
}

// Interactive task creation form shown inline in the chat
function TaskCreationForm({ clients, onSubmit, onCancel, prefill = {} as Record<string,any> }) {
  const [taskData, setTaskData] = useState({
    title: prefill.title || '',
    description: '',
    client_name: prefill.client_name || '',
    priority: prefill.priority || 'medium',
    category: prefill.category || 'general',
    due_date: prefill.due_date || '',
    status: 'pending',
  });

  const [clientSearch, setClientSearch] = useState(prefill.client_name || '');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef(null);

  const filteredClients = clients.filter(client =>
    client.full_name.toLowerCase().includes(clientSearch.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize client search when taskData.client_name changes
  useEffect(() => {
    if (taskData.client_name) {
      setClientSearch(taskData.client_name);
    } else {
      setClientSearch('');
    }
  }, [taskData.client_name]);

  const handleClientSelect = (client) => {
    if (client.full_name === '__none__') {
      setTaskData(prev => ({ ...prev, client_name: '' }));
      setClientSearch('No client (Internal task)');
    } else {
      setTaskData(prev => ({ ...prev, client_name: client.full_name }));
      setClientSearch(client.full_name);
    }
    setShowClientDropdown(false);
  };

  const categories = [
    { value: 'follow_up', label: 'Follow Up' },
    { value: 'review', label: 'Review' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'document', label: 'Document' },
    { value: 'compliance', label: 'Compliance' },
    { value: 'recommendation', label: 'Recommendation' },
    { value: 'general', label: 'General' },
    { value: 'internal_process', label: 'Internal Process' },
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-700' },
    { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
    { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' },
  ];

  const statuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'waiting_on_client', label: 'Waiting on Client' },
    { value: 'pending_review', label: 'Pending Review' },
  ];

  const handleSubmit = () => {
    if (!taskData.title.trim()) return;
    
    // Prepare final task data
    const finalTaskData = {
      ...taskData,
      // Use the actual client_name from taskData, not the search term
      client_name: taskData.client_name || null
    };
    
    onSubmit(finalTaskData);
  };

  return (
    <Card className="border-2 border-indigo-200 bg-gradient-to-b from-indigo-50 to-white">
      <CardContent className="p-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <CheckSquare className="w-4 h-4 text-indigo-600" />
            <p className="text-sm font-semibold text-indigo-900">Create New Task</p>
          </div>

          {/* Title */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-600">Title *</Label>
            <Input
              placeholder="e.g., Review annual portfolio report"
              value={taskData.title}
              onChange={(e) => setTaskData(prev => ({ ...prev, title: e.target.value }))}
              className="h-8 text-sm bg-white border-slate-300"
            />
          </div>

          {/* Description */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-600">Description</Label>
            <Textarea
              placeholder="Add task details..."
              value={taskData.description}
              onChange={(e) => setTaskData(prev => ({ ...prev, description: e.target.value }))}
              className="text-sm bg-white border-slate-300 min-h-[60px] resize-none"
              rows={2}
            />
          </div>

          {/* Client */}
          <div className="space-y-1 relative" ref={clientDropdownRef}>
            <Label className="text-xs font-medium text-slate-600">Client</Label>
            <div className="relative">
              <Input
                placeholder="Search clients or type 'No client'"
                value={clientSearch}
                onChange={(e) => {
                  setClientSearch(e.target.value);
                  setShowClientDropdown(true);
                }}
                onFocus={() => setShowClientDropdown(true)}
                className="h-8 text-sm bg-white border-slate-300 pr-8"
              />
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              
              {showClientDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-300 rounded-md shadow-lg z-[9999] max-h-48 overflow-y-auto">
                  <div
                    className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm border-b border-slate-200"
                    onClick={() => handleClientSelect({ full_name: '__none__' })}
                  >
                    No client (Internal task)
                  </div>
                  {filteredClients.length > 0 ? (
                    filteredClients.map((client) => (
                      <div
                        key={client.id}
                        className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                        onClick={() => handleClientSelect(client)}
                      >
                        {client.full_name}
                      </div>
                    ))
                  ) : clientSearch && (
                    <div className="px-3 py-2 text-sm text-slate-500">
                      No clients found matching "{clientSearch}"
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Priority & Category row */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-600">Priority</Label>
              <Select
                value={taskData.priority}
                onValueChange={(val) => setTaskData(prev => ({ ...prev, priority: val }))}
              >
                <SelectTrigger className="h-8 text-sm bg-white border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  {priorities.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${p.value === 'low' ? 'bg-slate-400' : p.value === 'medium' ? 'bg-blue-500' : p.value === 'high' ? 'bg-orange-500' : 'bg-red-500'}`} />
                        {p.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-600">Category</Label>
              <Select
                value={taskData.category}
                onValueChange={(val) => setTaskData(prev => ({ ...prev, category: val }))}
              >
                <SelectTrigger className="h-8 text-sm bg-white border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  {categories.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Due Date & Status row */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-600">Due Date</Label>
              <DatePicker
                value={taskData.due_date}
                onChange={(value) => setTaskData(prev => ({ ...prev, due_date: value }))}
                buttonClassName="h-8 text-sm border-slate-300"
                disablePast
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-600">Status</Label>
              <Select
                value={taskData.status}
                onValueChange={(val) => setTaskData(prev => ({ ...prev, status: val }))}
              >
                <SelectTrigger className="h-8 text-sm bg-white border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  {statuses.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!taskData.title.trim()}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
            >
              <Check className="w-3 h-3 mr-1" />
              Review & Create
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onCancel}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AppointmentCreationForm({ clients, onSubmit, onCancel, prefill = {} as Record<string,any> }) {
  const [apptData, setApptData] = useState({
    title: prefill.title || '',
    description: '',
    client_name: prefill.client_name || '',
    date: prefill.date || '',
    start_time: prefill.start_time || '09:00',
    duration_minutes: 60,
    meeting_type: prefill.meeting_type || 'video_call',
  });

  const [clientSearch, setClientSearch] = useState(prefill.client_name || '');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef(null);

  const filteredClients = clients.filter(c =>
    c.full_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(clientSearch.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setClientSearch(apptData.client_name || '');
  }, [apptData.client_name]);

  const meetingTypes = [
    { value: 'video_call', label: 'Video Call' },
    { value: 'phone_call', label: 'Phone Call' },
    { value: 'in_person', label: 'In Person' },
  ];

  const durations = [
    { value: 15, label: '15 min' },
    { value: 30, label: '30 min' },
    { value: 45, label: '45 min' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' },
  ];

  const handleSubmit = () => {
    if (!apptData.title.trim() || !apptData.date) return;

    const [h, m] = apptData.start_time.split(':').map(Number);
    const startDate = new Date(`${apptData.date}T${apptData.start_time}:00`);
    const endDate = new Date(startDate.getTime() + apptData.duration_minutes * 60000);
    const pad = (n) => String(n).padStart(2, '0');
    const endTime = `${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;

    onSubmit({
      title: apptData.title,
      description: apptData.description,
      client_name: apptData.client_name || null,
      start_time: `${apptData.date}T${apptData.start_time}:00`,
      end_time: `${apptData.date}T${endTime}:00`,
      meeting_type: apptData.meeting_type,
    });
  };

  return (
    <Card className="border-2 border-violet-200 bg-gradient-to-b from-violet-50 to-white">
      <CardContent className="p-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <CalendarIcon className="w-4 h-4 text-violet-600" />
            <p className="text-sm font-semibold text-violet-900">Schedule Appointment</p>
          </div>

          {/* Title */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-600">Title *</Label>
            <Input
              placeholder="e.g., Quarterly review with client"
              value={apptData.title}
              onChange={(e) => setApptData(prev => ({ ...prev, title: e.target.value }))}
              className="h-8 text-sm bg-white border-slate-300"
            />
          </div>

          {/* Client */}
          <div className="space-y-1 relative" ref={clientDropdownRef}>
            <Label className="text-xs font-medium text-slate-600">Client</Label>
            <div className="relative">
              <Input
                placeholder="Search by name or email…"
                value={clientSearch}
                onChange={(e) => { setClientSearch(e.target.value); setShowClientDropdown(true); }}
                onFocus={() => setShowClientDropdown(true)}
                className="h-8 text-sm bg-white border-slate-300 pr-8"
              />
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              {showClientDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-300 rounded-md shadow-lg z-[9999] max-h-48 overflow-y-auto">
                  <div
                    className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm border-b border-slate-200 text-slate-500"
                    onClick={() => { setApptData(prev => ({ ...prev, client_name: '' })); setClientSearch(''); setShowClientDropdown(false); }}
                  >
                    No client
                  </div>
                  {filteredClients.length > 0 ? filteredClients.map(c => (
                    <div
                      key={c.id}
                      className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                      onClick={() => { setApptData(prev => ({ ...prev, client_name: c.full_name })); setClientSearch(c.full_name); setShowClientDropdown(false); }}
                    >
                      <span className="font-medium">{c.full_name}</span>
                      {c.email && <span className="text-slate-400 text-xs ml-2">{c.email}</span>}
                    </div>
                  )) : clientSearch ? (
                    <div className="px-3 py-2 text-sm text-slate-500">No clients found</div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {/* Date + Start Time */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-600">Date *</Label>
              <Input
                type="date"
                value={apptData.date}
                onChange={(e) => setApptData(prev => ({ ...prev, date: e.target.value }))}
                className="h-8 text-sm bg-white border-slate-300"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-600">Start Time</Label>
              <Input
                type="time"
                value={apptData.start_time}
                onChange={(e) => setApptData(prev => ({ ...prev, start_time: e.target.value }))}
                className="h-8 text-sm bg-white border-slate-300"
              />
            </div>
          </div>

          {/* Duration + Meeting Type */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-600">Duration</Label>
              <Select
                value={String(apptData.duration_minutes)}
                onValueChange={(val) => setApptData(prev => ({ ...prev, duration_minutes: Number(val) }))}
              >
                <SelectTrigger className="h-8 text-sm bg-white border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  {durations.map(d => (
                    <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-600">Meeting Type</Label>
              <Select
                value={apptData.meeting_type}
                onValueChange={(val) => setApptData(prev => ({ ...prev, meeting_type: val }))}
              >
                <SelectTrigger className="h-8 text-sm bg-white border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  {meetingTypes.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-600">Notes</Label>
            <Textarea
              placeholder="Agenda or additional details…"
              value={apptData.description}
              onChange={(e) => setApptData(prev => ({ ...prev, description: e.target.value }))}
              className="text-sm bg-white border-slate-300 min-h-[50px] resize-none"
              rows={2}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!apptData.title.trim() || !apptData.date}
              className="flex-1 bg-violet-600 hover:bg-violet-700"
            >
              <Check className="w-3 h-3 mr-1" />
              Review & Schedule
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function OliAssistant({ isOpen, onToggle, onMinimize, isMinimized }) {
  const timezone = useTimezone();
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      role: "assistant",
      content: "Hi — I'm Oli, Vestolio's Intelligence Layer.\nI help you understand and use every feature of this platform, create records, and automate your work.\n\nAsk me anything:\n\n• \"How do I create a task?\" — Step-by-step guidance\n• \"What does the health score mean?\" — Feature explanations\n• \"Create a case for John's onboarding\" — I'll do it for you\n• \"What do the badges on automations mean?\" — UI explanations\n• \"Build a campaign for inactive clients\" — Full campaign created\n• \"Explain the difference between taskflows and workflows\" — Clear comparison\n\nI can create tasks, cases, goals, notes, campaigns, workflows, and more — just ask!",
      timestamp: new Date().toISOString()
    }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showWorkflowExecutor, setShowWorkflowExecutor] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [trainingMode, setTrainingMode] = useState(false);
  const [feedbackFor, setFeedbackFor] = useState(null);
  // Tracks pending action when Oli has asked for details and is waiting for user's reply
  // e.g. { actionType: 'create_task', phase: 'gathering_details' }
  const [pendingAction, setPendingAction] = useState(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const queryClient = useQueryClient();

  // Fetch data for context
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.entities.Client.list('-created_date'),
    enabled: isOpen,
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });

  const { data: campaigns = [] } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api.entities.Campaign.list('-created_date'),
    enabled: isOpen,
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });

  const { data: forms = [] } = useQuery({
    queryKey: ['forms'],
    queryFn: () => api.entities.Form.list('-created_date'),
    enabled: isOpen,
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });

  const { data: responses = [] } = useQuery({
    queryKey: ['all-responses'],
    queryFn: () => api.entities.Response.list('-created_date'),
    enabled: isOpen,
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });

  const { data: actions = [] } = useQuery({
    queryKey: ['all-tasks'],
    queryFn: () => api.entities.Task.list('-created_date'),
    enabled: isOpen,
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });

  const { data: enrollments = [] } = useQuery({
    queryKey: ['all-enrollments'],
    queryFn: () => api.entities.CampaignEnrollment.list('-created_date'),
    enabled: isOpen,
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });

  const { data: activities = [] } = useQuery({
    queryKey: ['all-activities'],
    queryFn: () => api.entities.ActivityLog.list('-created_date'),
    enabled: isOpen,
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });

  const { data: goals = [] } = useQuery({
    queryKey: ['all-goals'],
    queryFn: () => api.entities.Goal.list('-created_date'),
    enabled: isOpen,
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });

  const { data: notes = [] } = useQuery({
    queryKey: ['all-notes'],
    queryFn: () => api.entities.Note.list('-created_date'),
    enabled: isOpen,
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });

  const { data: appointments = [] } = useQuery({
    queryKey: ['appointments'],
    queryFn: () => api.entities.Appointment.list('-created_date'),
    enabled: isOpen,
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });

  const { data: communications = [] } = useQuery({
    queryKey: ['communications'],
    queryFn: () => api.entities.Communication.list('-created_date'),
    enabled: isOpen,
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });

  const { data: cases = [] } = useQuery({
    queryKey: ['cases'],
    queryFn: () => api.entities.Case.list('-created_date'),
    enabled: isOpen,
  });

  const { data: caseTemplates = [] } = useQuery({
    queryKey: ['case-templates'],
    queryFn: () => api.entities.CaseTemplate.list('-created_date'),
    enabled: isOpen,
  });

  const { data: workflows = [] } = useQuery({
    queryKey: ['workflow-templates'],
    queryFn: () => api.entities.WorkflowTemplate.list('-created_date'),
    enabled: isOpen,
  });

  const { data: taskflows = [] } = useQuery({
    queryKey: ['taskflows'],
    queryFn: () => api.entities.Taskflow.list('-created_date'),
    enabled: isOpen,
  });

  const { data: allTasks = [] } = useQuery({
    queryKey: ['all-case-tasks'],
    queryFn: () => api.entities.Task.list('-created_date', 200),
    enabled: isOpen,
  });

  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        if (scrollRef.current) {
          const scrollContainer = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
          if (scrollContainer) {
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
          }
        }
      }, 100);
    }
  }, [messages]);

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  const detectActionIntent = (userInput, hasFiles) => {
    const lower = userInput.toLowerCase();
    
    // Launch workflow detection
    if (lower.match(/launch|start|run|execute|open/) && lower.match(/workflow/)) {
      return 'launch_workflow';
    }
    
    // Help/explain/how-to questions → always go to general conversation (with knowledge base)
    if (lower.match(/how (do|can|to|does)|what (is|are|does|do)|explain|tell me about|help me understand|what's the difference|guide|walk me through|show me how|where (do|can|is)|when (should|do)|why (is|does|should)/)) {
      return 'general';
    }

    // Document-based workflow creation
    if (hasFiles && (lower.match(/create|build|generate|make/) && lower.match(/workflow|process|template/))) {
      return 'create_workflow_from_document';
    }
    
    // Data query detection
    if (lower.match(/show|find|list|which|what|identify|analyze/) && 
        (lower.match(/client|score|trend|performance|segment|data|metric/) ||
         lower.match(/declining|improving|at.risk|churn|predict/))) {
      return 'data_query';
    }
    
    if (lower.match(/segment|segmentation|group|categorize|classify/) && lower.match(/client/)) {
      return 'segment_clients';
    }
    if (lower.match(/create|build|design|make|generate/) && lower.match(/form|assessment|survey|quiz/)) {
      return 'create_form';
    }
    if (lower.match(/create|build|design|make|generate/) && lower.match(/campaign|marketing|email.sequence|nurture/)) {
      return 'create_campaign';
    }
    if (lower.match(/create|add|new/) && lower.match(/client|person/)) {
      return 'create_client';
    }
    if (lower.match(/create|add|new|draft|write/) && lower.match(/note/)) {
      return 'create_note';
    }
    if (lower.match(/create|add|new/) && lower.match(/task|action|todo|follow.up|reminder/)) {
      return 'create_task';
    }
    if ((lower.match(/create|add|new|book|schedule/) && lower.match(/appoi|meeting|call/)) ||
        (lower.match(/schedule/) && lower.match(/with|at|for|on/))) {
      return 'create_appointment';
    }
    if (lower.match(/create|add|new|set/) && lower.match(/goal/)) {
      return 'create_goal';
    }
    if (lower.match(/create|add|new|draft|write|send/) && lower.match(/email|communication|message/)) {
      return 'create_communication';
    }
    if (lower.match(/create|add|new|build/) && lower.match(/case/)) {
      return 'create_case';
    }
    if (lower.match(/create|add|new|build/) && lower.match(/case.template/)) {
      return 'create_case_template';
    }
    if (lower.match(/create|add|new|build|design/) && lower.match(/internal.workflow|workflow.template/) && !lower.match(/from.document|document/)) {
      return 'create_internal_workflow';
    }
    if (lower.match(/create|add|new|build|design/) && lower.match(/taskflow/)) {
      return 'create_taskflow';
    }
    if (lower.match(/update|edit|change|modify/) && lower.match(/client|note|task|goal|appointment|case/)) {
      return 'update_entity';
    }
    
    return 'general';
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    try {
      const fileUrls = [];
      for (const file of files) {
        const { file_url } = await api.integrations.Core.UploadFile({ file });
        fileUrls.push(file_url);
      }
      setUploadedFiles(prev => [...prev, ...fileUrls]);
      
      const fileMessage = {
        id: Date.now().toString(),
        role: "assistant",
        content: `✓ Uploaded ${files.length} file(s). I can now analyze these documents. What would you like me to do with them?`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, fileMessage]);
    } catch (error) {
      console.error('File upload error:', error);
      const errorMessage = {
        id: Date.now().toString(),
        role: "assistant",
        content: `Failed to upload file(s): ${error.message}`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSendMessage = async () => {
    if ((!input.trim() && uploadedFiles.length === 0) || isLoading) return;

    // Handle training feedback submission
    if (trainingMode && feedbackFor) {
      const feedbackMessage = {
        id: Date.now().toString(),
        role: "user",
        content: input,
        timestamp: new Date().toISOString(),
        isFeedback: true
      };
      setMessages(prev => [...prev, feedbackMessage]);

      try {
        await api.entities.OliPersonalization.create({
          scope: "user",
          scope_id: user.email,
          title: "Feedback on Response",
          content: `Better response: ${input}`,
          category: "general",
          created_by: user.email,
          created_by_name: user.full_name,
          feedback_data: {
            original_query: feedbackFor.originalQuery,
            original_response: feedbackFor.content,
            feedback: input,
            improved_response: input
          }
        });

        const confirmMsg = {
          id: Date.now().toString(),
          role: "assistant",
          content: "Thank you! I've saved your feedback and will learn from it to improve future responses.",
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, confirmMsg]);
      } catch (error) {
        const errorMsg = {
          id: Date.now().toString(),
          role: "assistant",
          content: "Sorry, I couldn't save the feedback. Please try again.",
          timestamp: new Date().toISOString(),
          isError: true
        };
        setMessages(prev => [...prev, errorMsg]);
      }

      setInput("");
      setTrainingMode(false);
      setFeedbackFor(null);
      return;
    }

    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      content: input || "📎 Attached files",
      timestamp: new Date().toISOString(),
      files: uploadedFiles.length > 0 ? [...uploadedFiles] : undefined
    };

    setMessages(prev => [...prev, userMessage]);
    const userInput = input;
    const filesForContext = [...uploadedFiles];
    setInput("");
    setUploadedFiles([]);
    setIsLoading(true);

    try {
      const context = {
        total_clients: clients.length,
        active_clients: clients.filter(c => c.status === 'active').length,
        lead_clients: clients.filter(c => c.status === 'lead').length,
        total_campaigns: campaigns.length,
        active_campaigns: campaigns.filter(c => c.status === 'active').length,
        total_forms: forms.length,
        total_tasks: actions.length,
        pending_actions: actions.filter(a => a.status === 'pending' || a.status === 'in_progress').length,
        total_cases: cases.length,
        active_cases: cases.filter(c => c.status === 'in_progress').length,
        total_case_templates: caseTemplates.length,
        total_taskflows: taskflows.length,
        active_taskflows: taskflows.filter(t => t.is_active).length,
        total_goals: goals.length,
        total_appointments: appointments.length,
        upcoming_appointments: appointments.filter(a => a.status === 'scheduled').length,
        total_communications: communications.length,
        total_workflows: workflows.length,
        total_notes: notes.length,
        client_names: clients.slice(0, 20).map(c => c.full_name),
      };

      // If Oli asked the user to confirm/pick a client, resolve it now and proceed directly to creation
      if (pendingAction && pendingAction.phase === 'confirming_client') {
        const savedAction = pendingAction;
        const reply = userInput.toLowerCase().trim();
        const isEmailReply = reply.includes('@');
        const confirmed = savedAction.candidates.find((c) =>
          isEmailReply
            ? (c.email || '').toLowerCase() === reply
            : c.full_name.toLowerCase().includes(reply) || reply.includes(c.full_name.toLowerCase())
        );
        if (confirmed) {
          setPendingAction(null);
          savedAction.structuredData.client_id = confirmed.id;
          savedAction.structuredData.client_name = confirmed.full_name;
          await performCreateAction(savedAction.actionType, savedAction.structuredData, savedAction.entityType, savedAction.successMessage);
        } else {
          // Still ambiguous — ask again
          const pickMsg = {
            id: Date.now().toString(),
            role: "assistant",
            content: `I still couldn't match that. Please type the exact name:\n${savedAction.candidates.map((c) => `• ${c.full_name}`).join('\n')}`,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, pickMsg]);
          // keep pendingAction unchanged so next reply tries again
        }
        return;
      }

      // If there's a pending action waiting for user details, route the reply there
      if (pendingAction && pendingAction.phase === 'gathering_details') {
        const savedAction = pendingAction;
        setPendingAction(null);
        const combinedInput = savedAction.originalQuery
          ? `${savedAction.originalQuery}\n\nUser's follow-up: ${userInput}`
          : userInput;
        await handleCreateAction(savedAction.actionType, combinedInput, context, true);
      } else {
        const actionIntent = detectActionIntent(userInput, filesForContext.length > 0);

        if (actionIntent === 'launch_workflow') {
          await handleLaunchWorkflow(userInput, context);
        } else if (actionIntent === 'create_workflow_from_document') {
          await handleWorkflowCreation(userInput, context, filesForContext);
        } else if (actionIntent === 'data_query') {
          await handleDataQuery(userInput, context, filesForContext);
        } else if (actionIntent === 'segment_clients') {
          await handleClientSegmentation(userInput, context);
        } else if (actionIntent === 'create_form') {
          await handleFormCreation(userInput, context);
        } else if (actionIntent === 'create_campaign') {
          await handleCampaignCreation(userInput, context);
        } else if (actionIntent === 'create_internal_workflow') {
          await handleInternalWorkflowCreation(userInput, context);
        } else if (actionIntent === 'create_taskflow') {
          await handleTaskflowCreation(userInput, context);
        } else if (actionIntent === 'create_case' || actionIntent === 'create_case_template') {
          await handleCreateAction(actionIntent, userInput, context);
        } else if (actionIntent !== 'general') {
          await handleCreateAction(actionIntent, userInput, context);
        } else {
          await handleRegularConversation(userInput, context, filesForContext);
        }
      }

    } catch (error) {
      console.error('Oli error:', error);
      let errorContent = "I apologize, but I encountered an error processing your request.";
      
      if (error.message?.includes('timeout')) {
        errorContent = "The request took too long to process. Please try:\n\n• A shorter, more specific request\n• Breaking your request into smaller parts\n• Trying again in a moment";
      } else if (error.message?.includes('network')) {
        errorContent = "Network error. Please check your internet connection and try again.";
      } else if (error.message) {
        errorContent = `Error: ${error.message}\n\nPlease try rephrasing your request or contact support if the issue persists.`;
      }
      
      const errorMessage = {
        id: Date.now().toString(),
        role: "assistant",
        content: errorContent,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLaunchWorkflow = async (userInput, context) => {
    const lower = userInput.toLowerCase();
    
    // Check if workflow name is specified
    let targetWorkflow = null;
    
    // Try to extract workflow name from quotes or after "workflow"
    const nameMatch = lower.match(/"([^"]+)"|'([^']+)'|workflow\s+(.+?)(?:\s|$)/);
    if (nameMatch) {
      const extractedName = (nameMatch[1] || nameMatch[2] || nameMatch[3] || '').trim();
      targetWorkflow = workflows.find(w => 
        w.name.toLowerCase().includes(extractedName) || 
        extractedName.includes(w.name.toLowerCase())
      );
    }
    
    // If workflow found, launch it
    if (targetWorkflow) {
      setSelectedWorkflow(targetWorkflow);
      setShowWorkflowExecutor(true);
      
      const successMsg = {
        id: Date.now().toString(),
        role: "assistant",
        content: `Launching "${targetWorkflow.name}" workflow. Complete the steps to finish the process.`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, successMsg]);
    } else {
      // Ask which workflow to launch
      if (workflows.length === 0) {
        const noWorkflowMsg = {
          id: Date.now().toString(),
          role: "assistant",
          content: "No workflows are currently available. You can create workflows in the Internal Workflows section.",
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, noWorkflowMsg]);
      } else {
        const workflowListMsg = {
          id: Date.now().toString(),
          role: "assistant",
          content: `Which workflow would you like to launch?\n\nAvailable workflows:\n${workflows.filter(w => w.is_active !== false).map((w, i) => `${i + 1}. ${w.name} - ${w.description || 'No description'}`).join('\n')}\n\nJust tell me the name or number.`,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, workflowListMsg]);
      }
    }
  };

  const handleCampaignCreation = async (userInput, context) => {
    const currentDate = nowInTimezone(timezone).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
    
    // Fetch analyzed client data from backend
    const analysisResponse = await api.functions.invoke('analyzeClientData', { limit: 30 });
    const { clientData } = analysisResponse.data;

    const campaignPrompt = `CURRENT DATE: ${currentDate}

You are an expert marketing campaign strategist. Create a complete campaign based on: "${userInput}"

CLIENT DATA (sample of ${clientData.length} clients):
${JSON.stringify(clientData, null, 2)}

Create a comprehensive campaign with:
- Clear name and goal
- Target audience criteria
- 3-5 message sequence
- Optimal timing
- Success metrics
- A/B test suggestions

Return JSON matching this structure exactly.`;

    const campaignSchema = {
      type: "object",
      properties: {
        campaign_name: { type: "string" },
        campaign_description: { type: "string" },
        campaign_type: { type: "string" },
        goal: { type: "string" },
        target_audience: {
          type: "object",
          properties: {
            description: { type: "string" },
            estimated_count: { type: "number" },
            criteria: {
              type: "object",
              properties: {
                score_min: { type: ["number", "null"] },
                score_max: { type: ["number", "null"] },
                client_status: { type: "array", items: { type: "string" } },
                has_completed_assessment: { type: ["boolean", "null"] }
              }
            }
          }
        },
        messages: {
          type: "array",
          items: {
            type: "object",
            properties: {
              name: { type: "string" },
              channel: { type: "string" },
              delay_days: { type: "number" },
              delay_hours: { type: "number" },
              subject: { type: "string" },
              content: { type: "string" }
            }
          }
        },
        success_metrics: {
          type: "object",
          properties: {
            expected_open_rate: { type: "number" },
            expected_engagement_rate: { type: "number" },
            target_outcome: { type: "string" }
          }
        },
        ab_test_suggestions: {
          type: "array",
          items: {
            type: "object",
            properties: {
              element: { type: "string" },
              variation_a: { type: "string" },
              variation_b: { type: "string" },
              test_hypothesis: { type: "string" }
            }
          }
        }
      }
    };

    const campaignData = await Promise.race([
      api.integrations.Core.InvokeLLM({
        prompt: campaignPrompt,
        response_json_schema: campaignSchema,
        add_context_from_internet: false
      }),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 45000)
      )
    ]);

    campaignData.messages = campaignData.messages.map((msg, idx) => ({
      ...msg,
      id: `msg_${Date.now()}_${idx}`,
      conditional_logic: { enabled: false, conditions: [] }
    }));

    const createdCampaign = await api.entities.Campaign.create({
      name: campaignData.campaign_name,
      description: campaignData.campaign_description,
      type: campaignData.campaign_type || 'custom',
      status: 'draft',
      config: {
        ai_generated: true,
        goal: campaignData.goal,
        trigger_conditions: {
          auto_enroll: true,
          ...campaignData.target_audience?.criteria
        },
        target_audience: campaignData.target_audience,
        messages: campaignData.messages,
        schedule_recommendation: campaignData.schedule_recommendation,
        success_metrics: campaignData.success_metrics,
        ab_test_suggestions: campaignData.ab_test_suggestions || [],
        strategy_notes: campaignData.strategy_notes || [],
      },
      stats: {
        enrolled_count: 0,
        active_count: 0,
        completed_count: 0
      }
    });

    // Auto-create Email/SMS Templates and link them as campaign assets
    for (let i = 0; i < campaignData.messages.length; i++) {
      const msg = campaignData.messages[i];
      const channel = (msg.channel || 'email').toLowerCase();
      try {
        if (channel === 'sms') {
          const template = await api.entities.SmsTemplate.create({
            name: msg.name || `Campaign SMS ${i + 1}`,
            body: msg.content,
            category: 'marketing',
            status: 'draft',
            is_active: true,
            variables: ['client_name'],
          });
          await api.entities.CampaignAssetLink.create({
            campaign_id: createdCampaign.id,
            asset_type: 'sms_template',
            asset_id: template.id,
            sort_order: i,
            is_active: true,
            data: { message_index: i, delay_days: msg.delay_days, best_send_time: msg.best_send_time },
          });
        } else {
          const template = await api.entities.EmailTemplate.create({
            name: msg.name || `Campaign Email ${i + 1}`,
            subject: msg.subject || '',
            body: msg.content,
            category: 'marketing',
            status: 'draft',
            is_active: true,
            variables: ['client_name'],
          });
          await api.entities.CampaignAssetLink.create({
            campaign_id: createdCampaign.id,
            asset_type: 'email_template',
            asset_id: template.id,
            sort_order: i,
            is_active: true,
            data: { message_index: i, delay_days: msg.delay_days, best_send_time: msg.best_send_time },
          });
        }
      } catch (err) {
        console.warn(`Failed to create template asset for message ${i + 1}:`, err);
      }
    }

    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    queryClient.invalidateQueries({ queryKey: ['sms-templates'] });

    const successMsg = {
      id: Date.now().toString(),
      role: "assistant",
      content: `Great! I've created your campaign "${campaignData.campaign_name}".\n\nKey Features:\n\n• ${campaignData.messages.length} personalized messages\n• Targeting ~${campaignData.target_audience.estimated_count} clients\n• Expected ${campaignData.success_metrics.expected_open_rate}% open rate\n• ${campaignData.ab_test_suggestions?.length || 0} A/B testing opportunities\n\nThe campaign is saved as a draft and ready for review!`,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, successMsg]);

    setTimeout(() => {
      const previewMsg = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "campaign_preview",
        timestamp: new Date().toISOString(),
        campaign: { ...campaignData, id: createdCampaign.id }
      };
      setMessages(prev => [...prev, previewMsg]);
    }, 300);

    setTimeout(() => {
      const followUpMsg = {
        id: (Date.now() + 2).toString(),
        role: "assistant",
        content: "Next Steps:\n\n• Review the message sequence in Campaigns tab\n• Adjust targeting criteria if needed\n• Test A/B variations for optimization\n• Activate when ready to auto-enroll clients\n\nWould you like me to help you with anything else?",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, followUpMsg]);
    }, 800);
  };

  const handleWorkflowCreation = async (userInput, context, fileUrls) => {
    const currentDate = nowInTimezone(timezone).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
    const workflowPrompt = `CURRENT DATE: ${currentDate}

You are an expert workflow designer for financial advisors. Analyze the uploaded document(s) and create a comprehensive internal workflow template for data collection.

User request: "${userInput}"

Create a workflow with:
1. A clear name and description
2. Sequential steps that collect all necessary information from the document
3. Appropriate step types (form_input for data collection, data_summary for review)
4. For each form field, include:
   - Field name (snake_case)
   - Clear label
   - Appropriate type (text, number, email, phone, date, textarea, radio, select)
   - Options array for radio/select types
   - Required flag

Workflow structure should follow this pattern:
- Step 1-N: Form inputs for each section of the document
- Final step: Data summary showing all collected information

Make the workflow thorough and professional, capturing all relevant fields from the document.`;

    const workflowSchema = {
      type: "object",
      properties: {
        name: { type: "string" },
        description: { type: "string" },
        category: { type: "string", enum: ["onboarding", "compliance", "financial_planning", "data_collection", "custom"] },
        steps: {
          type: "array",
          items: {
            type: "object",
            properties: {
              id: { type: "string" },
              title: { type: "string" },
              description: { type: "string" },
              step_type: { type: "string", enum: ["form_input", "data_summary"] },
              order: { type: "number" },
              configuration: {
                type: "object",
                properties: {
                  fields: {
                    type: "array",
                    items: {
                      type: "object",
                      properties: {
                        name: { type: "string" },
                        label: { type: "string" },
                        type: { type: "string" },
                        required: { type: "boolean" },
                        options: { type: "array", items: { type: "string" } }
                      }
                    }
                  },
                  summary_fields: {
                    type: "array",
                    items: {
                      type: "object",
                      properties: {
                        label: { type: "string" },
                        source_step_id: { type: "string" },
                        source_field_path: { type: "string" },
                        format: { type: "string" }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    };

    const workflowData = await Promise.race([
      api.integrations.Core.InvokeLLM({
        prompt: workflowPrompt,
        response_json_schema: workflowSchema,
        file_urls: fileUrls,
        add_context_from_internet: false
      }),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 50000)
      )
    ]);

    // Create the workflow template
    const createdWorkflow = await api.entities.WorkflowTemplate.create({
      ...workflowData,
      is_active: true
    });

    queryClient.invalidateQueries({ queryKey: ['workflow-templates'] });

    const successMsg = {
      id: Date.now().toString(),
      role: "assistant",
      content: `Excellent! I've created the workflow template "${workflowData.name}".\n\nKey Features:\n\n• ${workflowData.steps.length} step(s) for comprehensive data collection\n• ${workflowData.steps.reduce((sum, step) => sum + (step.configuration?.fields?.length || 0), 0)} input fields extracted from document\n• Structured data summary at the end\n• Ready to use for ${workflowData.category} processes\n\nThe workflow is now available in your Internal Workflows section!`,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, successMsg]);

    setTimeout(() => {
      const followUpMsg = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "Next Steps:\n\n• Review the workflow in Internal Workflows\n• Test with sample data to ensure accuracy\n• Adjust fields or steps as needed\n• Use it to streamline your data collection process\n\nWould you like me to help you with anything else?",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, followUpMsg]);
    }, 800);
  };

  const handleDataQuery = async (userInput, context, fileUrls = []) => {
    const currentDate = nowInTimezone(timezone).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
    
    // Fetch analyzed client data from backend
    const analysisResponse = await api.functions.invoke('analyzeClientData', { limit: 50 });
    const { clientData, summary } = analysisResponse.data;

    const queryPrompt = `CURRENT DATE: ${currentDate}

You are an expert financial advisor data analyst. Answer this specific query about client data.

QUERY: "${userInput}"

COMPREHENSIVE DATA SUMMARY:
${JSON.stringify(summary, null, 2)}

DETAILED CLIENT DATA (sample of ${clientData.length}):
${JSON.stringify(clientData, null, 2)}

ANALYSIS REQUIREMENTS:
1. Answer the user's specific question directly and precisely
2. Identify relevant clients by name when appropriate
3. Provide specific numbers and percentages
4. Include trend analysis if requested
5. Offer predictive insights if the query asks for predictions
6. Highlight any risks or opportunities
7. Suggest actionable next steps

Provide a clear, data-driven answer that directly addresses their question.`;

    const response = await Promise.race([
      api.integrations.Core.InvokeLLM({
        prompt: queryPrompt,
        add_context_from_internet: false,
        file_urls: fileUrls.length > 0 ? fileUrls : undefined
      }),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 30000)
      )
    ]);

    const assistantMessage = {
      id: Date.now().toString(),
      role: "assistant",
      content: response,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, assistantMessage]);

    // Follow-up suggestion
    setTimeout(() => {
      const followUpMsg = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "Would you like me to:\n\n• Create a targeted campaign for these clients\n• Generate a detailed report\n• Segment clients based on this analysis\n• Draft recommendations for specific clients",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, followUpMsg]);
    }, 800);
  };

  const handleFormCreation = async (userInput, context) => {
    const currentDate = nowInTimezone(timezone).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
    const buildPrompt = `CURRENT DATE: ${currentDate}

You are an expert financial advisor form designer. Create a comprehensive form based on this request: "${userInput}"

QUESTIONNAIRE DESIGN GUIDELINES:

1. PURPOSE ANALYSIS:
   - Onboarding: Welcome questions, baseline assessment, demographic info
   - Risk Assessment: Financial situation, goals, risk tolerance
   - Financial Health: Comprehensive 5-pillar evaluation
   - Retirement Planning: Retirement goals, savings, timeline
   - Tax Planning: Income sources, deductions, tax optimization
   - Client Feedback: Service quality, satisfaction, improvements
   - Annual Review: Progress tracking, goal updates

2. QUESTION TYPES TO USE:
   - scale: For ratings (1-10) - use for agreement, importance, confidence
   - yes_no: Binary decisions - use for qualification, status checks
   - single_choice: One option from list - use for categories, preferences
   - multiple_choice: Multiple selections - use for complex situations
   - text: Short answers - use for names, numbers, brief responses
   - long_text: Detailed responses - use for explanations, concerns, goals

3. PILLAR ASSIGNMENT:
   - planning: Budgets, goals, financial plans, cash flow
   - investing: Portfolios, risk tolerance, investment strategy
   - protection: Insurance, emergency funds, estate planning
   - tax: Tax efficiency, deductions, optimization
   - retirement: Retirement savings, planning, timeline

4. SCORING MECHANISM:
   - Set max_score for quantifiable questions (typically 10 or 100)
   - Weight important questions higher (1-10, where 10 is most critical)
   - For scale questions: answer value = score
   - For yes_no: yes=max_score, no=0
   - For choice questions: assign scores to options
   - Create thresholds for each pillar:
     * excellent: 80-100%
     * good: 60-79%
     * needs_improvement: <60%

5. QUESTION STRUCTURE:
   - Start with basic info/context questions
   - Progress to more detailed/complex questions
   - Group questions by pillar when logical
   - End with open-ended feedback
   - Include 8-15 questions for focused questionnaires
   - Include 20-30 questions for comprehensive assessments

6. BEST PRACTICES:
   - Clear, concise question text
   - Avoid jargon (or explain it)
   - Provide help_text for complex questions
   - Logical flow and progression
   - Balance question types
   - Make questions actionable

Create a complete questionnaire with:
- Title and description
- 10-20 well-structured questions
- Appropriate question types
- Pillar assignments
- Scoring setup with max_score and weight
- Threshold definitions for each pillar
- Help text where needed

Make it professional, comprehensive, and immediately usable.`;

    const questionnaireSchema = {
      type: "object",
      properties: {
        title: { type: "string" },
        description: { type: "string" },
        questions: {
          type: "array",
          items: {
            type: "object",
            properties: {
              id: { type: "string" },
              question_text: { type: "string" },
              pillar: { type: "string", enum: ["planning", "investing", "protection", "tax", "retirement"] },
              question_type: { type: "string", enum: ["scale", "yes_no", "single_choice", "multiple_choice", "text", "long_text"] },
              options: { type: "array", items: { type: "string" } },
              max_score: { type: "number" },
              weight: { type: "number" },
              required: { type: "boolean" },
              help_text: { type: "string" }
            }
          }
        },
        thresholds: {
          type: "object",
          properties: {
            planning: {
              type: "object",
              properties: {
                excellent: { type: "number" },
                good: { type: "number" },
                needs_improvement: { type: "number" }
              }
            },
            investing: {
              type: "object",
              properties: {
                excellent: { type: "number" },
                good: { type: "number" },
                needs_improvement: { type: "number" }
              }
            },
            protection: {
              type: "object",
              properties: {
                excellent: { type: "number" },
                good: { type: "number" },
                needs_improvement: { type: "number" }
              }
            },
            tax: {
              type: "object",
              properties: {
                excellent: { type: "number" },
                good: { type: "number" },
                needs_improvement: { type: "number" }
              }
            },
            retirement: {
              type: "object",
              properties: {
                excellent: { type: "number" },
                good: { type: "number" },
                needs_improvement: { type: "number" }
              }
            }
          }
        }
      }
    };

    const questionnaireData = await Promise.race([
      api.integrations.Core.InvokeLLM({
        prompt: buildPrompt,
        response_json_schema: questionnaireSchema,
        add_context_from_internet: false
      }),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 40000)
      )
    ]);

    // Create the form
    try {
      const createdForm = await api.entities.Form.create({
        ...questionnaireData,
        form_type: 'assessment',
        is_active: true,
        current_version: 1
      });

      queryClient.invalidateQueries({ queryKey: ['forms'] });

      // Success message
      const successMsg = {
        id: Date.now().toString(),
        role: "assistant",
        content: `Great! I've created your form "${questionnaireData.title}".\n\nKey Features:\n\n• ${questionnaireData.questions.length} thoughtfully designed questions\n• Covers all 5 financial health pillars\n• Smart scoring mechanism with weighted questions\n• Clear performance thresholds\n• Mix of question types for comprehensive assessment\n\nThe form is now active and ready to be assigned to clients!`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, successMsg]);

      // Show preview
      setTimeout(() => {
        const previewMsg = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: "questionnaire_preview",
          timestamp: new Date().toISOString(),
          questionnaire: createdForm
        };
        setMessages(prev => [...prev, previewMsg]);
      }, 300);

      // Follow-up suggestions
      setTimeout(() => {
        const followUpMsg = {
          id: (Date.now() + 2).toString(),
          role: "assistant",
          content: "Next Steps:\n\n• Review the form questions\n• Assign it to clients for completion\n• Monitor responses in the dashboard\n• Use results for client segmentation\n• Create targeted campaigns based on scores\n\nWould you like me to help you assign this to specific clients or create a campaign?",
          timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, followUpMsg]);
        }, 800);

      } catch (error) {
      console.error('Form creation error:', error);
      const errorMsg = {
        id: Date.now().toString(),
        role: "assistant",
        content: `I encountered an error creating the form. Please try again.\n\nError: ${error.message}`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
      }
      };

  const handleInternalWorkflowCreation = async (userInput, context) => {
    const currentDate = nowInTimezone(timezone).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
    
    const workflowPrompt = `CURRENT DATE: ${currentDate}

You are an expert workflow designer for financial advisors. Create a comprehensive internal workflow template based on: "${userInput}"

WORKFLOW DESIGN GUIDELINES:

1. WORKFLOW STRUCTURE:
   - Name and description should be clear and actionable
   - Category options: onboarding, compliance, financial_planning, data_collection, custom
   - Steps should follow a logical sequence

2. STEP TYPES AVAILABLE:
   - form_input: Collect data from user with form fields
   - entity_creation: Create new entities (clients, tasks, goals, documents)
   - ai_guidance: Provide AI-powered suggestions or analysis
   - data_summary: Display collected data for review
   - data_mapping: Map collected data to client financial data
   - conditional_branch: Branch based on conditions

3. FORM INPUT STEP CONFIGURATION:
   - Fields: name (snake_case), label, type, required, options (for select/radio)
   - Field types: text, number, email, phone, date, textarea, radio, select, checkbox
   - Group related fields logically
   - Include help text for complex fields

4. ENTITY CREATION STEP:
   - Specify entity_type: Client, Task, Goal, Document, Note
   - Map fields from previous steps to entity fields
   - Use dot notation for nested data (e.g., step_1.client_name)

5. AI GUIDANCE STEP:
   - Provide context about what analysis to perform
   - Specify what insights to generate
   - Make prompts specific and actionable

6. DATA MAPPING STEP:
   - Map workflow data to ClientFinancialData structure
   - Specify target paths (e.g., income_sources, assets_detail)
   - Include validation rules

7. BEST PRACTICES:
   - Start with data collection steps
   - Follow with entity creation or AI analysis
   - End with summary or confirmation
   - Keep workflows focused (3-8 steps ideal)
   - Make steps reusable when possible

Create a complete workflow with 3-7 well-designed steps that accomplish the user's goal efficiently.`;

    const workflowSchema = {
      type: "object",
      properties: {
        name: { type: "string" },
        description: { type: "string" },
        category: { type: "string", enum: ["onboarding", "compliance", "financial_planning", "data_collection", "custom"] },
        steps: {
          type: "array",
          items: {
            type: "object",
            properties: {
              id: { type: "string" },
              title: { type: "string" },
              description: { type: "string" },
              step_type: { type: "string", enum: ["form_input", "entity_creation", "ai_guidance", "data_summary", "data_mapping", "conditional_branch"] },
              order: { type: "number" },
              configuration: {
                type: "object",
                properties: {
                  fields: {
                    type: "array",
                    items: {
                      type: "object",
                      properties: {
                        name: { type: "string" },
                        label: { type: "string" },
                        type: { type: "string" },
                        required: { type: "boolean" },
                        options: { type: "array", items: { type: "string" } },
                        help_text: { type: "string" }
                      }
                    }
                  },
                  entity_type: { type: "string" },
                  field_mapping: { type: "object" },
                  ai_context: { type: "string" },
                  summary_fields: { type: "array" }
                }
              }
            }
          }
        },
        tags: { type: "array", items: { type: "string" } }
      }
    };

    const workflowData = await Promise.race([
      api.integrations.Core.InvokeLLM({
        prompt: workflowPrompt,
        response_json_schema: workflowSchema,
        add_context_from_internet: false
      }),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 40000)
      )
    ]);

    const createdWorkflow = await api.entities.WorkflowTemplate.create({
      ...workflowData,
      is_active: true
    });

    queryClient.invalidateQueries({ queryKey: ['workflow-templates'] });

    const successMsg = {
      id: Date.now().toString(),
      role: "assistant",
      content: `Excellent! I've created the internal workflow "${workflowData.name}".\n\nKey Features:\n\n• ${workflowData.steps.length} step(s) for ${workflowData.category}\n• ${workflowData.steps.filter(s => s.step_type === 'form_input').length} data collection step(s)\n• ${workflowData.steps.filter(s => s.step_type === 'entity_creation').length} entity creation step(s)\n• ${workflowData.steps.filter(s => s.step_type === 'ai_guidance').length} AI analysis step(s)\n• Ready to use in cases and client workflows\n\nThe workflow template is now available in Internal Workflows!`,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, successMsg]);

    setTimeout(() => {
      const followUpMsg = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "Next Steps:\n\n• Review the workflow in Internal Workflows\n• Test it with sample data\n• Add it to case templates for automation\n• Launch it directly from cases as needed\n\nWould you like me to help you create anything else?",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, followUpMsg]);
    }, 800);
  };

  const handleTaskflowCreation = async (userInput, context) => {
    const currentDate = nowInTimezone(timezone).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
    
    const taskflowPrompt = `CURRENT DATE: ${currentDate}

You are an expert taskflow designer for financial advisors. Create a comprehensive taskflow based on: "${userInput}"

TASKFLOW DESIGN GUIDELINES:

1. TASKFLOW BASICS:
   - Name and description should clearly explain the workflow
   - Category: onboarding, compliance, client_service, operations, financial_planning
   - Creation mode: auto_create (tasks created automatically on dependency completion) or create_all_upfront (all tasks created immediately) or manual (advisor creates tasks manually)
   - Sequential: true if tasks must be done in order, false if parallel

2. STEP STRUCTURE:
   - Each step becomes a task when created
   - step_id: unique identifier (e.g., "step_1", "step_2")
   - step_name: Clear task title
   - is_group: false for tasks, true for visual grouping only
   - depends_on_step_ids: Array of step_ids that must complete first (for sequential flow)
   - assigned_to_role: Who should do this (e.g., "Advisor", "Operations", "Support")
   - assigned_to_team_id: Optional team assignment
   - is_automated: true if task triggers next step automatically

3. DEPENDENCY MANAGEMENT:
   - For sequential taskflows: each step depends on previous step
   - For parallel taskflows: steps can run independently
   - Example sequential: step_2 depends_on ["step_1"], step_3 depends_on ["step_2"]
   - Example parallel: all steps have empty depends_on_step_ids

4. CREATION MODES:
   - auto_create: Create first step immediately, then create next steps when dependencies complete
   - create_all_upfront: Create all tasks at once, mark dependent tasks as "not_ready"
   - manual: Advisor manually creates tasks from the taskflow template

5. BEST PRACTICES:
   - 3-10 steps for focused taskflows
   - Clear step names that describe the action
   - Assign to appropriate roles
   - Set logical dependencies
   - Use groups to organize related steps visually

Create a complete taskflow with 3-8 well-designed steps.`;

    const taskflowSchema = {
      type: "object",
      properties: {
        name: { type: "string" },
        description: { type: "string" },
        category: { type: "string", enum: ["onboarding", "compliance", "client_service", "operations", "financial_planning"] },
        creation_mode: { type: "string", enum: ["auto_create", "create_all_upfront", "manual"] },
        is_sequential: { type: "boolean" },
        create_first_step_immediately: { type: "boolean" },
        steps: {
          type: "array",
          items: {
            type: "object",
            properties: {
              step_id: { type: "string" },
              step_name: { type: "string" },
              is_group: { type: "boolean" },
              depends_on_step_ids: { type: "array", items: { type: "string" } },
              assigned_to_role: { type: "string" },
              is_automated: { type: "boolean" }
            }
          }
        }
      }
    };

    const taskflowData = await Promise.race([
      api.integrations.Core.InvokeLLM({
        prompt: taskflowPrompt,
        response_json_schema: taskflowSchema,
        add_context_from_internet: false
      }),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 40000)
      )
    ]);

    const createdTaskflow = await api.entities.Taskflow.create({
      ...taskflowData,
      is_active: true
    });

    queryClient.invalidateQueries({ queryKey: ['taskflows'] });

    const successMsg = {
      id: Date.now().toString(),
      role: "assistant",
      content: `Perfect! I've created the taskflow "${taskflowData.name}".\n\nKey Features:\n\n• ${taskflowData.steps.length} step(s) in the sequence\n• Creation mode: ${taskflowData.creation_mode.replace('_', ' ')}\n• ${taskflowData.is_sequential ? 'Sequential execution (one at a time)' : 'Parallel execution (all at once)'}\n• Category: ${taskflowData.category}\n• ${taskflowData.create_first_step_immediately ? 'First step creates immediately' : 'Waits for trigger'}\n\nThe taskflow is ready to be added to cases!`,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, successMsg]);

    setTimeout(() => {
      const followUpMsg = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "Next Steps:\n\n• Review the taskflow in Task Configuration\n• Adjust dependencies or assignments if needed\n• Add it to case templates for automation\n• Use it when creating new cases\n\nWould you like me to help you with anything else?",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, followUpMsg]);
    }, 800);
  };

  const handleClientSegmentation = async (userInput, context) => {
    const currentDate = nowInTimezone(timezone).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
    
    // Fetch analyzed client data from backend
    const analysisResponse = await api.functions.invoke('analyzeClientData', { limit: 50 });
    const { clientData, summary } = analysisResponse.data;

    const analysisPrompt = `CURRENT DATE: ${currentDate}

You are an expert financial advisor analyst. Analyze the following client data and create meaningful client segments.

Client Data Summary:
${JSON.stringify(summary, null, 2)}

Detailed Client Data (sample of ${clientData.length}):
${JSON.stringify(clientData, null, 2)}

User Request: "${userInput}"

Create 4-6 distinct client segments based on:
1. Financial health scores (high/low performers)
2. Engagement level (active, disengaged, at-risk)
3. Growth potential (high-potential, stable, needs attention)
4. Demographics and relationship status if relevant
5. Campaign engagement
6. Recommendation completion rates

For each segment, provide:
- A clear segment name
- Number of clients in segment
- Percentage of total
- 3-5 key characteristics
- 2-4 sample client names
- 3-5 specific outreach strategies

Be specific and actionable. Focus on segments that help the advisor take targeted action.`;

    const segmentSchema = {
      type: "object",
      properties: {
        segments: {
          type: "array",
          items: {
            type: "object",
            properties: {
              id: { type: "string" },
              name: { type: "string" },
              description: { type: "string" },
              client_count: { type: "number" },
              percentage: { type: "number" },
              characteristics: {
                type: "array",
                items: { type: "string" }
              },
              sample_clients: {
                type: "array",
                items: { type: "string" }
              },
              strategies: {
                type: "array",
                items: { type: "string" }
              }
            }
          }
        },
        summary: { type: "string" }
      }
    };

    const segmentData = await Promise.race([
      api.integrations.Core.InvokeLLM({
        prompt: analysisPrompt,
        response_json_schema: segmentSchema,
        add_context_from_internet: false
      }),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 40000)
      )
    ]);

    const summaryMessage = {
      id: Date.now().toString(),
      role: "assistant",
      content: segmentData.summary || `I've analyzed your ${summary.total_clients} clients and identified ${segmentData.segments.length} distinct segments. Each segment has specific characteristics and tailored outreach strategies.`,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, summaryMessage]);

    setTimeout(() => {
      const segmentsMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "client_segments",
        timestamp: new Date().toISOString(),
        segments: segmentData.segments
      };
      setMessages(prev => [...prev, segmentsMessage]);
    }, 300);

    setTimeout(() => {
      const followUpMessage = {
        id: (Date.now() + 2).toString(),
        role: "assistant",
        content: "Next Steps:\n\n• Review each segment's characteristics\n• Implement the recommended strategies\n• Create targeted campaigns for specific segments\n• Track engagement and adjust strategies\n• Re-segment quarterly to monitor changes\n\nWould you like me to help you create a campaign for any specific segment?",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, followUpMessage]);
    }, 800);
  };

  const handleCreateAction = async (actionType, userInput, context, hasGatheredDetails = false) => {
     // Check if confirmation is required
     const requireConfirmation = userPreferences?.require_oli_confirmation_before_create !== false;

     let schema, entityType, successMessage;

     switch (actionType) {
      case 'create_client':
        schema = {
          type: "object",
          properties: {
            full_name: { type: "string" },
            email: { type: "string" },
            phone: { type: "string" },
            status: { type: "string", enum: ["active", "inactive", "pending", "lead"] },
            advisor_notes: { type: "string" }
          },
          required: ["full_name", "email"]
        };
        entityType = "Client";
        successMessage = "client";
        break;

      case 'create_note':
        schema = {
          type: "object",
          properties: {
            client_name: { type: "string" },
            note_type: { type: "string", enum: ["general", "meeting", "call", "action_item", "follow_up", "recommendation"] },
            content: { type: "string" },
            visibility: { type: "string", enum: ["internal", "external"] },
            priority: { type: "string", enum: ["low", "medium", "high"] },
            mentioned_users: { type: "array", items: { type: "string" }, description: "Names of users to mention/notify" }
          },
          required: ["client_name", "content"]
        };
        entityType = "Note";
        successMessage = "note";
        break;

      case 'create_task':
        schema = {
          type: "object",
          properties: {
            client_name: { type: "string" },
            title: { type: "string" },
            description: { type: "string" },
            category: { type: "string", enum: ["follow_up", "review", "meeting", "document", "compliance", "recommendation", "general", "internal_process", "taskflow_step"] },
            priority: { type: "string", enum: ["low", "medium", "high", "urgent"] },
            due_date: { type: "string" },
            status: { type: "string", enum: ["pending", "in_progress", "completed", "cancelled", "waiting_on_client", "pending_review"] },
            assigned_to: { type: "string" },
            pillar: { type: "string", enum: ["income", "investing", "protection", "tax", "retirement", "estate"] },
            client_visible: { type: "boolean" }
          },
          required: ["title"]
        };
        entityType = "Task";
        successMessage = "task";
        break;

      case 'create_goal':
        schema = {
          type: "object",
          properties: {
            client_name: { type: "string" },
            title: { type: "string" },
            description: { type: "string" },
            category: { type: "string", enum: ["retirement", "education", "home", "emergency_fund", "debt_payoff", "investment", "tax_savings", "estate", "other"] },
            target_amount: { type: "number" },
            current_amount: { type: "number" },
            target_date: { type: "string" },
            priority: { type: "string", enum: ["low", "medium", "high"] }
          },
          required: ["client_name", "title", "target_amount"]
        };
        entityType = "Goal";
        successMessage = "goal";
        break;

      case 'create_appointment':
        schema = {
          type: "object",
          properties: {
            client_name: { type: "string" },
            title: { type: "string" },
            description: { type: "string" },
            start_time: { type: "string" },
            end_time: { type: "string" },
            meeting_type: { type: "string", enum: ["in_person", "video_call", "phone_call"] },
            location: { type: "string" }
          },
          required: ["title", "start_time", "end_time"]
        };
        entityType = "Appointment";
        successMessage = "appointment";
        break;

      case 'create_communication':
        schema = {
          type: "object",
          properties: {
            client_name: { type: "string" },
            subject: { type: "string" },
            content: { type: "string" },
            communication_type: { type: "string", enum: ["email", "sms"] },
            purpose: { type: "string", enum: ["review_reminder", "portfolio_update", "market_news", "recommendation_follow_up", "check_in", "milestone_celebration", "educational_content", "general"] }
          },
          required: ["client_name", "subject", "content", "communication_type"]
        };
        entityType = "Communication";
        successMessage = "communication";
        break;

      case 'create_case':
        schema = {
          type: "object",
          properties: {
            client_name: { type: "string" },
            title: { type: "string" },
            description: { type: "string" },
            case_type: { type: "string", enum: ["onboarding", "review_preparation", "account_opening", "financial_planning", "tax_preparation", "estate_planning", "compliance", "issue_resolution", "custom"] },
            priority: { type: "string", enum: ["low", "medium", "high", "urgent"] },
            due_date: { type: "string" }
          },
          required: ["client_name", "title", "case_type"]
        };
        entityType = "Case";
        successMessage = "case";
        break;

      case 'create_case_template':
        schema = {
          type: "object",
          properties: {
            name: { type: "string" },
            description: { type: "string" },
            case_type: { type: "string", enum: ["onboarding", "review_preparation", "account_opening", "financial_planning", "tax_preparation", "estate_planning", "compliance", "issue_resolution", "custom"] },
            default_priority: { type: "string", enum: ["low", "medium", "high", "urgent"] },
            estimated_duration_days: { type: "number" }
          },
          required: ["name", "case_type"]
        };
        entityType = "CaseTemplate";
        successMessage = "case template";
        break;

      default:
        await handleRegularConversation(userInput, context);
        return;
    }

    // Show interactive forms immediately — before any API calls or LLM extraction
    if (entityType === "Task" && !hasGatheredDetails) {
      const prefillData = extractTaskPrefill(userInput, clients);
      setMessages(prev => [...prev, { id: Date.now().toString(), role: "assistant", content: "task_form", prefillData, timestamp: new Date().toISOString() }]);
      return;
    }
    if (entityType === "Appointment" && !hasGatheredDetails) {
      const prefillData = extractAppointmentPrefill(userInput, clients);
      setMessages(prev => [...prev, { id: Date.now().toString(), role: "assistant", content: "appointment_form", prefillData, timestamp: new Date().toISOString() }]);
      return;
    }

    const currentDate = nowInTimezone(timezone).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });

    // Fetch users for mention support
    const allUsers = (await api.entities.User.list()).filter(u => {
      const role = String(u?.role || '').toLowerCase();
      return ['admin', 'advisor', 'super_admin'].includes(role)
        && u?.is_active !== false
        && u?.isActive !== false
        && Boolean(u?.last_login_at || u?.lastLoginAt);
    });
    
    const firmInstruction = oliConfig.mergedSystemPrompt
      ? `\nFIRM CONTEXT: ${oliConfig.firmConfig?.firm_name || "Vestolio Enterprise"} — apply firm-specific terminology and instructions when extracting details.\n`
      : "";
    let dataPrompt = `CURRENT DATE: ${currentDate}${firmInstruction}

Extract the following information from this request: "${userInput}"

Context: ${JSON.stringify(context)}

Available clients: ${clients.slice(0, 10).map(c => c.full_name).join(', ')}
Available users for mentions: ${allUsers.slice(0, 20).map(u => u.full_name).join(', ')}`;

    // Enhanced prompts for notes
    if (actionType === 'create_note') {
      dataPrompt += `

NOTE CREATION GUIDELINES:
- Extract note content, type, priority, and visibility
- Parse @mentions from the user input (e.g., "Create note for John mentioning @Sarah about the review")
- mentioned_users should be an array of user NAMES (not emails) that were mentioned with @ symbol
- If user says "mention Sarah" or "@Sarah", add "Sarah" to mentioned_users array
- Note types: general, meeting, call, action_item, follow_up, recommendation
- Priority: low/medium/high
- Visibility: internal (advisor only) or external (client can see)

Example: "Create a note for John mentioning @Sarah and @Mike about the quarterly review"
Extraction:
{
  "client_name": "John",
  "content": "Discussed quarterly review with John. Follow up needed on retirement projections.",
  "note_type": "meeting",
  "priority": "medium",
  "visibility": "internal",
  "mentioned_users": ["Sarah", "Mike"]
}`;
    }

    // Enhanced prompts for tasks
    if (actionType === 'create_task') {
      dataPrompt += `

TASK CREATION GUIDELINES:
- Tasks are ACTION ITEMS, TO-DOs, or FOLLOW-UPs for clients
- Tasks are NOT appointments or meetings (those are different)
- Extract task details: title, description, category, priority, due date
- Category types: follow_up (call/email client), review (review documents/forms), meeting (prepare for meeting), document (collect/review docs), compliance (regulatory tasks), recommendation (implement advice), general (other tasks), internal_process (internal work), taskflow_step (part of larger workflow)
- Priority: low/medium/high/urgent based on urgency and importance
- Status: pending (not started), in_progress (actively working), completed, cancelled, waiting_on_client, pending_review
- Due date: extract from request or set reasonable default
- Client: ONLY match to client name if EXPLICITLY specified in request. If no client is mentioned, set client_name to null
- Assigned to: advisor email if specified
- Pillar: only set if task relates to specific financial pillar (income/investing/protection/tax/retirement/estate)
- client_visible: true if client should see this task in their portal, false for internal tasks

Example task request: "Create a task to follow up with John about his 401k rollover"
Example extraction:
{
  "title": "Follow up on 401k rollover",
  "description": "Call John to discuss 401k rollover options and next steps",
  "client_name": "John",
  "category": "follow_up",
  "priority": "high",
  "due_date": "[3 days from now]",
  "status": "pending",
  "pillar": "retirement",
  "client_visible": true
}

Example task request WITHOUT client specified: "Create a task to review quarterly reports"
Example extraction (no client assumed):
{
  "title": "Review quarterly reports",
  "description": "Review and analyze quarterly reports",
  "client_name": null,
  "category": "review",
  "priority": "medium",
  "due_date": "[5 days from now]",
  "status": "pending",
  "client_visible": false
}`;
    }

    dataPrompt += `

Provide the data in the exact format requested. If information is missing, use reasonable defaults.`;

    // Check if request is too vague and needs clarification questions
    const isTooVague = (input, entityType) => {
      const lower = input.trim().toLowerCase();
      const tooShort = lower.split(' ').length < 4;
      const hasNoDetail = !lower.match(/for|about|with|named|regarding|to|on|by|due|called/);
      if (tooShort && hasNoDetail) return true;
      const exactVague = [
        "create me a task", "create a task", "add a task", "make a task", "new task",
        "create me a note", "create a note", "add a note", "make a note",
        "create me a goal", "create a goal", "add a goal", "make a goal",
        "create me a case", "create a case", "add a case", "make a case",
        "create me an appointment", "create an appointment", "add an appointment", "schedule a meeting",
        "create me a communication", "create a communication", "send a message",
        "create me a client", "add a client", "new client",
      ];
      return exactVague.includes(lower);
    };

    if (isTooVague(userInput, entityType)) {
      let questions = "";

      if (entityType === "Task") {
        questions = `To create a task, I need a few details:

    1. What should the task be about? (task title/description)
    2. Is this for a specific client? If so, which one?
    3. How urgent is it? (low, medium, high, urgent)
    4. When is it due?
    5. What category? (follow_up, review, meeting, document, etc.)

    Tell me about the task you want to create!`;
      } else if (entityType === "Note") {
        questions = `To create a note, I need some details:

    1. What is the note about?
    2. Which client does it relate to?
    3. What type? (general, meeting, call, action_item, follow_up, recommendation)
    4. How important? (low, medium, high)
    5. Who should see it? (internal or visible to client)

    Give me the details and I'll create it!`;
      } else if (entityType === "Goal") {
        questions = `To create a goal, I need these details:

    1. What is the goal? (e.g., "Save for down payment on house")
    2. Which client?
    3. Target amount?
    4. Target date?
    5. Category? (retirement, education, home, emergency_fund, etc.)

    Tell me about the goal!`;
      } else if (entityType === "Case") {
        questions = `To create a case, I need:

    1. What is this case about? (case title)
    2. Which client?
    3. What type? (onboarding, review_preparation, financial_planning, tax_preparation, etc.)
    4. How urgent? (low, medium, high, urgent)
    5. When should it be completed?

    What case do you want to create?`;
      } else if (entityType === "Appointment") {
        questions = `To schedule an appointment, I need:

    1. What is the meeting about? (title)
    2. Which client? (or is it internal?)
    3. What date and time?
    4. Type? (video_call, phone_call, in_person)
    5. How long should it be?

    Give me the appointment details!`;
      } else if (entityType === "Communication") {
        questions = `To create a communication, I need:

    1. Who is it for? (which client)
    2. What's the subject?
    3. What's the message content?
    4. How should I send it? (email or SMS)
    5. What's the purpose? (review reminder, check_in, etc.)

    Tell me what you want to communicate!`;
      }

      const clarificationMessage = {
        id: Date.now().toString(),
        role: "assistant",
        content: questions,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, clarificationMessage]);
      setPendingAction({ phase: 'gathering_details', actionType, originalQuery: userInput });
      return;
    }

    const structuredData = await Promise.race([
       api.integrations.Core.InvokeLLM({
         prompt: dataPrompt,
         response_json_schema: schema,
         add_context_from_internet: false
       }),
       new Promise((_, reject) => 
         setTimeout(() => reject(new Error('Request timeout')), 20000)
       )
     ]);

     // Show confirmation message if required
     if (requireConfirmation) {
       const confirmationMsg = {
         id: Date.now().toString(),
         role: "assistant",
         content: `confirmation_pending`,
         timestamp: new Date().toISOString(),
         actionType,
         structuredData,
         entityType: entityType,
         successMessage
       };
       setMessages(prev => [...prev, confirmationMsg]);
       return;
     }

     // For client-based entities without a client specified, ask for clarification
     const clientRequiredEntities = ["Task", "Note", "Goal", "Case", "Communication", "Appointment"];

     if (clientRequiredEntities.includes(entityType) && !structuredData.client_name) {
       let itemDescription = "";

       if (entityType === "Task") {
         itemDescription = `task: "${structuredData.title}"`;
       } else if (entityType === "Note") {
         itemDescription = `note`;
       } else if (entityType === "Goal") {
         itemDescription = `goal: "${structuredData.title}"`;
       } else if (entityType === "Case") {
         itemDescription = `case: "${structuredData.title}"`;
       } else if (entityType === "Communication") {
         itemDescription = `communication: "${structuredData.subject}"`;
       } else if (entityType === "Appointment") {
         itemDescription = `appointment: "${structuredData.title}"`;
       }

       const clarificationMessage = {
         id: Date.now().toString(),
         role: "assistant",
         content: `I understand you want to create a ${itemDescription}.\n\nWhich client is this related to?\n\nAvailable clients:\n${clients.slice(0, 15).map(c => `• ${c.full_name}`).join('\n')}${clients.length > 15 ? `\n\n... and ${clients.length - 15} more clients` : ''}`,
         timestamp: new Date().toISOString()
       };
       setMessages(prev => [...prev, clarificationMessage]);
       setPendingAction({ phase: 'gathering_details', actionType, originalQuery: userInput });
       return;
     }

    if ((entityType === "Note" || entityType === "Task") && structuredData.client_name) {
      const name = structuredData.client_name.toLowerCase().trim();
      const isEmail = name.includes('@');
      const searchWords = name.split(/\s+/).filter(w => w.length > 1);
      const isMultiWord = searchWords.length > 1;
      const candidates = clients.filter(c => {
        const cname = (c.full_name || '').toLowerCase();
        if (isEmail) {
          return (c.email || '').toLowerCase() === name || cname.includes(name);
        }
        if (isMultiWord) {
          return searchWords.every(w => cname.includes(w));
        }
        return cname.includes(name) || name.includes(cname);
      });

      if (candidates.length === 0) {
        const clarificationMessage = {
          id: Date.now().toString(),
          role: "assistant",
          content: `I couldn't find a client named "${structuredData.client_name}". Could you please specify which client this is for?\n\nAvailable clients:\n${clients.slice(0, 10).map(c => `• ${c.full_name}`).join('\n')}`,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, clarificationMessage]);
        setPendingAction({ phase: 'gathering_details', actionType, originalQuery: userInput });
        return;
      } else if (candidates.length === 1) {
        const isExact = candidates[0].full_name.toLowerCase() === name;
        if (isExact) {
          structuredData.client_id = candidates[0].id;
          structuredData.client_name = candidates[0].full_name;
        } else {
          const sm = entityType === "Task" ? "task" : "note";
          const pickerMsg = {
            id: Date.now().toString(),
            role: "assistant",
            content: "client_picker",
            timestamp: new Date().toISOString(),
            promptText: `Did you mean one of these clients?`,
            candidates,
            actionType,
            structuredData: { ...structuredData },
            entityType,
            successMessage: sm
          };
          setMessages(prev => [...prev, pickerMsg]);
          setPendingAction({ phase: 'confirming_client', actionType, structuredData, entityType, successMessage: sm, candidates });
          return;
        }
      } else {
        const sm = entityType === "Task" ? "task" : "note";
        const pickerMsg = {
          id: Date.now().toString(),
          role: "assistant",
          content: "client_picker",
          timestamp: new Date().toISOString(),
          promptText: `I found multiple clients matching "${structuredData.client_name}". Which one did you mean?`,
          candidates,
          actionType,
          structuredData: { ...structuredData },
          entityType,
          successMessage: sm
        };
        setMessages(prev => [...prev, pickerMsg]);
        setPendingAction({ phase: 'confirming_client', actionType, structuredData, entityType, successMessage: sm, candidates });
        return;
      }
    }

    try {
      await performCreateAction(actionType, structuredData, entityType, successMessage);
    } catch (error) {
      console.error('Creation error:', error);
      const errorMsg = {
        id: Date.now().toString(),
        role: "assistant",
        content: `I encountered an error creating the item. Please check the details and try again.\n\nError: ${error.message}`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
    }
  };

  const performCreateAction = async (actionType, structuredData, entityType, successMessage) => {
    try {
      let createdEntity;

      // Resolve client_id from client_name or email — confirm with user when ambiguous
      const clientLinkedEntities = ["Task", "Note", "Goal", "Appointment", "Communication", "Case"];
      if (clientLinkedEntities.includes(entityType) && structuredData.client_name && !structuredData.client_id) {
        const name = structuredData.client_name.toLowerCase().trim();
        const isEmail = name.includes('@');
        const searchWords = name.split(/\s+/).filter(w => w.length > 1);
        const isMultiWord = searchWords.length > 1;
        const candidates = clients.filter(c => {
          const cname = (c.full_name || '').toLowerCase();
          if (isEmail) {
            return (c.email || '').toLowerCase() === name || cname.includes(name);
          }
          if (isMultiWord) {
            // All search words must be present in the client's full name — prevents "kumar" matching "Manoj Kumar"
            return searchWords.every(w => cname.includes(w));
          }
          return cname.includes(name) || name.includes(cname);
        });

        if (candidates.length === 0) {
          // No match — fall through; entity-specific handlers will show the not-found message
        } else if (candidates.length === 1) {
          const isExact = candidates[0].full_name.toLowerCase() === name;
          if (isExact) {
            // Exact match — resolve silently
            structuredData.client_id = candidates[0].id;
            structuredData.client_name = candidates[0].full_name;
          } else {
            // Single partial match — show picker to confirm
            const pickerMsg = {
              id: Date.now().toString(),
              role: "assistant",
              content: "client_picker",
              timestamp: new Date().toISOString(),
              promptText: `Did you mean one of these clients?`,
              candidates,
              actionType,
              structuredData: { ...structuredData },
              entityType,
              successMessage
            };
            setMessages(prev => [...prev, pickerMsg]);
            setPendingAction({ phase: 'confirming_client', actionType, structuredData, entityType, successMessage, candidates });
            return;
          }
        } else {
          // Multiple matches — show picker
          const pickerMsg = {
            id: Date.now().toString(),
            role: "assistant",
            content: "client_picker",
            timestamp: new Date().toISOString(),
            promptText: `I found multiple clients matching "${structuredData.client_name}". Which one did you mean?`,
            candidates,
            actionType,
            structuredData: { ...structuredData },
            entityType,
            successMessage
          };
          setMessages(prev => [...prev, pickerMsg]);
          setPendingAction({ phase: 'confirming_client', actionType, structuredData, entityType, successMessage, candidates });
          return;
        }
      }

      if (entityType === "Client") {
        createdEntity = await api.entities.Client.create({
          ...structuredData,
          status: structuredData.status || "active"
        });
        
        await api.entities.ActivityLog.create({
          client_id: createdEntity.id,
          client_name: createdEntity.full_name,
          activity_type: "profile_updated",
          description: "Client profile created by Oli AI assistant",
          performed_by: "Oli (AI)"
        });

      } else if (entityType === "Note") {
        const mentionEmails = [];
        if (structuredData.mentioned_users && Array.isArray(structuredData.mentioned_users)) {
          const allUsers = (await api.entities.User.list()).filter(u => {
            const role = String(u?.role || '').toLowerCase();
            return ['admin', 'advisor', 'super_admin'].includes(role)
              && u?.is_active !== false
              && u?.isActive !== false
              && Boolean(u?.last_login_at || u?.lastLoginAt);
          });
          
          for (const mentionedName of structuredData.mentioned_users) {
            const matchedUser = allUsers.find(u => 
              u.full_name.toLowerCase().includes(mentionedName.toLowerCase()) ||
              mentionedName.toLowerCase().includes(u.full_name.toLowerCase())
            );
            if (matchedUser && !mentionEmails.includes(matchedUser.email)) {
              mentionEmails.push(matchedUser.email);
            }
          }
        }

        const currentUserEmail = (user?.email || '').trim().toLowerCase();
        const mentionRecipients = mentionEmails.filter(mentionedEmail =>
          mentionedEmail && mentionedEmail.trim().toLowerCase() !== currentUserEmail
        );

        createdEntity = await api.entities.Note.create({
          ...structuredData,
          note_type: structuredData.note_type || "general",
          visibility: structuredData.visibility || "internal",
          priority: structuredData.priority || "medium",
          author_email: user?.email,
          author_name: user?.full_name,
          mentions: mentionRecipients
        });

        if (mentionRecipients.length > 0) {
          for (const mentionedEmail of mentionRecipients) {
            try {
              await api.functions.invoke('dispatchNotification', {
                user_email: mentionedEmail,
                type: 'mention',
                notification_type: 'mention',
                title: `${user?.full_name || 'Oli AI'} mentioned you in a note`,
                message: `"${structuredData.content.substring(0, 100)}${structuredData.content.length > 100 ? '...' : ''}"`,
                action_url: `/ClientDetail?id=${structuredData.client_id}&tab=notes`,
                action_label: 'View Note',
                related_entity_id: createdEntity.id,
                related_entity_type: 'Note',
                priority: 'medium'
              });
            } catch (error) {
              console.error('Failed to send mention notification:', error);
            }
          }
        }

        await api.entities.ActivityLog.create({
          client_id: structuredData.client_id,
          client_name: structuredData.client_name,
          activity_type: "note_added",
          description: "Note added by Oli AI assistant",
          performed_by: "Oli (AI)"
        });

      } else if (entityType === "Task") {
        createdEntity = await api.entities.Task.create({
          ...structuredData,
          status: structuredData.status || "pending",
          priority: structuredData.priority || "medium",
          category: structuredData.category || "general",
          client_visible: structuredData.client_visible !== false
        });

        if (structuredData.client_id) {
          await api.entities.ActivityLog.create({
            client_id: structuredData.client_id,
            client_name: structuredData.client_name,
            activity_type: "note_added",
            description: `Task created by Oli AI: ${structuredData.title}`,
            performed_by: "Oli (AI)"
          });
        }

      } else if (entityType === "Goal") {
        createdEntity = await api.entities.Goal.create({
          ...structuredData,
          status: "not_started",
          priority: structuredData.priority || "medium",
          current_amount: structuredData.current_amount || 0
        });

      } else if (entityType === "Appointment") {
        createdEntity = await api.entities.Appointment.create({
          ...structuredData,
          status: "scheduled",
          meeting_type: structuredData.meeting_type || "video_call",
          created_by: user?.email,
          created_by_name: user?.full_name,
        });

      } else if (entityType === "Communication") {
        createdEntity = await api.entities.Communication.create({
          ...structuredData,
          status: "draft",
          drafted_by: "oli_ai",
          purpose: structuredData.purpose || "general"
        });

      } else if (entityType === "Case") {
        if (!structuredData.client_id) {
          const clarificationMessage = {
            id: Date.now().toString(),
            role: "assistant",
            content: `I couldn't find a client named "${structuredData.client_name}". Could you please specify which client this case is for?\n\nAvailable clients:\n${clients.slice(0, 10).map(c => `• ${c.full_name}`).join('\n')}`,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, clarificationMessage]);
          return;
        }

        createdEntity = await api.entities.Case.create({
          ...structuredData,
          status: "not_started",
          priority: structuredData.priority || "medium",
          task_ids: [],
          workflow_ids: [],
          task_workflow_ids: [],
          completed_tasks: 0,
          total_tasks: 0,
          progress_percentage: 0
        });

      } else if (entityType === "CaseTemplate") {
        createdEntity = await api.entities.CaseTemplate.create({
          ...structuredData,
          is_active: true,
          default_priority: structuredData.default_priority || "medium",
          estimated_duration_days: structuredData.estimated_duration_days || 30
        });
      }

      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['clients-search'] });
      queryClient.invalidateQueries({ queryKey: ['all-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case-templates'] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['all-goals'] });
      queryClient.invalidateQueries({ queryKey: ['goals'] });
      queryClient.invalidateQueries({ queryKey: ['communications'] });
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      queryClient.invalidateQueries({ queryKey: ['all-notes'] });

      const successMsg = {
        id: Date.now().toString(),
        role: "assistant",
        content: `Great! I've successfully created the ${successMessage}.\n\nDetails:\n${formatEntityDetails(entityType, createdEntity, structuredData)}`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, successMsg]);

      setTimeout(() => {
        const confirmMsg = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: "action_completed",
          timestamp: new Date().toISOString(),
          entityType,
          entity: createdEntity
        };
        setMessages(prev => [...prev, confirmMsg]);
      }, 300);

    } catch (error) {
      console.error('Creation error:', error);
      const errorMsg = {
        id: Date.now().toString(),
        role: "assistant",
        content: `I encountered an error creating the item. Please check the details and try again.\n\nError: ${error.message}`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
    }
  };

  const formatEntityDetails = (entityType, entity, data) => {
    switch (entityType) {
      case "Client":
        return `• Name: ${entity.full_name}\n• Email: ${entity.email}\n• Phone: ${entity.phone || 'Not provided'}\n• Status: ${entity.status}`;
      
      case "Note":
        const mentionsList = data.mentioned_users?.length > 0 ? `\n• Mentions: ${data.mentioned_users.join(', ')}` : '';
        return `• Client: ${data.client_name}\n• Type: ${data.note_type}\n• Priority: ${data.priority}\n• Visibility: ${data.visibility}${mentionsList}`;
      
      case "Task":
        return `• Client: ${data.client_name || 'Internal task'}\n• Title: ${data.title}\n• Description: ${data.description || 'No description'}\n• Category: ${data.category}\n• Priority: ${data.priority}\n• Status: ${data.status}\n• Due: ${data.due_date || 'Not set'}\n• Assigned to: ${data.assigned_to || 'Unassigned'}`;
      
      case "Goal":
        return `• Client: ${data.client_name}\n• Title: ${data.title}\n• Category: ${data.category}\n• Target Amount: $${data.target_amount.toLocaleString()}\n• Target Date: ${data.target_date || 'Not set'}`;
      
      case "Appointment":
        return `• Title: ${data.title}\n• Client: ${data.client_name || 'Internal'}\n• Start: ${data.start_time}\n• Type: ${data.meeting_type}`;
      
      case "Communication":
        return `• Client: ${data.client_name}\n• Subject: ${data.subject}\n• Type: ${data.communication_type}\n• Status: Draft (ready to send)`;
      
      case "Case":
        return `• Client: ${data.client_name}\n• Title: ${data.title}\n• Type: ${data.case_type}\n• Priority: ${data.priority}\n• Due: ${data.due_date || 'Not set'}`;
      
      case "CaseTemplate":
        return `• Name: ${data.name}\n• Type: ${data.case_type}\n• Priority: ${data.default_priority}\n• Duration: ${data.estimated_duration_days} days`;
      
      default:
        return "Created successfully";
    }
  };

  const handleRegularConversation = async (userInput, context, fileUrls = []) => {
    const currentDate = nowInTimezone(timezone).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
    // Inject only firm config + training knowledge — NOT the governance contract format
    // (the full mergedSystemPrompt includes a response contract that the LLM would output literally)
    const firmLayerParts = [];
    if (oliConfig.firmConfig?.firm_name) {
      firmLayerParts.push(`Firm: ${oliConfig.firmConfig.firm_name}`);
    }
    if (oliConfig.firmConfig?.tone_preference) {
      firmLayerParts.push(`Tone: ${oliConfig.firmConfig.tone_preference}`);
    }
    if (oliConfig.firmConfig?.custom_instructions) {
      firmLayerParts.push(`Firm instructions: ${oliConfig.firmConfig.custom_instructions}`);
    }
    if (oliConfig.firmTerminology) {
      firmLayerParts.push(`Firm terminology: ${oliConfig.firmTerminology}`);
    }
    if (oliConfig.trainingData?.length > 0) {
      firmLayerParts.push("Firm training knowledge:");
      oliConfig.trainingData.slice(0, 30).forEach((item) => {
        firmLayerParts.push(`- ${item.title || "Training item"}: ${item.content || item.description || ""}`);
      });
    }
    const firmLayer = firmLayerParts.length > 0
      ? `\n\n═══════════════════════════════════════════════════\nFIRM CONTEXT\n═══════════════════════════════════════════════════\n${firmLayerParts.join("\n")}\n`
      : "";
    const prompt = `CURRENT DATE: ${currentDate}${firmLayer}

You are Oli, Vestolio's AI Intelligence Layer — an expert assistant built into a wealth management platform for financial advisors. You have deep knowledge of every feature, page, status, badge, and workflow in this application. You help advisors understand the platform, navigate features, and execute actions.

═══════════════════════════════════════════════════
APPLICATION KNOWLEDGE BASE
═══════════════════════════════════════════════════

PAGES & FEATURES:

1. CLIENTS PAGE (/Clients):
   - Manage all client relationships
   - Statuses: "active" (engaged client), "inactive" (dormant), "lead" (prospect not yet onboarded), "prospect" (in pipeline)
   - Health Score: 0-100% circular gauge showing overall financial health across 5 pillars
   - Segments: Tags like "High Value", "At Risk", "New Client" for grouping clients
   - Actions: Add Client, view details, filter by status/segment
   - HOW TO CREATE A CLIENT: Click "+ Add Client" button or ask me "create a client named [name]"

2. TASKS PAGE (/Tasks):
   - All action items, to-dos, and follow-ups
   - Statuses: "pending" (not started), "in_progress" (actively working), "completed" (done), "cancelled", "waiting_on_client" (blocked on client action), "pending_review" (needs advisor review)
   - Priority badges: "low" (gray), "medium" (blue), "high" (orange), "urgent" (red)
   - Categories: follow_up, review, meeting, document, compliance, recommendation, general, internal_process, taskflow_step
   - Pillars: income, investing, protection, tax, retirement, estate — indicating which financial area the task relates to
   - HOW TO CREATE A TASK: Click "+ Add Task" button, or ask me "create a task to [description] for [client name]"
   - Tasks can be standalone or part of a Taskflow/Case

3. GOALS PAGE (/Goals):
   - Track client financial goals with progress
   - Categories: retirement, education, home, emergency_fund, debt_payoff, investment, tax_savings, estate, other
   - Shows target amount, current amount, progress percentage
   - Priority: low, medium, high
   - HOW TO CREATE A GOAL: Click "+ Add Goal" or ask me "create a goal for [client] to save [amount] for [purpose]"

4. CASES PAGE (/Cases):
   - Project management for client matters (onboarding, reviews, planning)
   - Case Types: onboarding, review_preparation, account_opening, financial_planning, tax_preparation, estate_planning, compliance, issue_resolution, custom
   - Statuses: "not_started", "in_progress", "completed", "on_hold", "cancelled"
   - Priority: low, medium, high, urgent
   - Cases contain Tasks, Taskflows, and Workflows
   - HOW TO CREATE A CASE: Click "+ New Case" or ask me "create a case for [client] for [purpose]"

5. CAMPAIGNS PAGE (/MarketingCampaigns):
   - Email/SMS marketing campaigns for client outreach
   - Statuses: "draft" (not yet active), "active" (running and auto-enrolling), "paused" (temporarily stopped), "completed" (finished run)
   - Contains message sequences with delays (Day 0, Day 3, Day 7, etc.)
   - Assets: Email templates and SMS templates linked to each message
   - Enrolled count: Number of clients in the campaign
   - HOW TO CREATE A CAMPAIGN: Click "+ Create Campaign" or ask me "create a campaign for [purpose targeting audience]"

6. AUTOMATIONS PAGE (/Automations):
   - Event-driven automation rules that trigger actions
   - Statuses: "active" (running), "paused" (stopped), "draft" (not activated), "archived"
   - Trigger Types: time_based, event_based, condition_based, manual
   - Target Types: client, task, goal, campaign, case, communication
   - Actions: send_email, create_task, update_field, enroll_campaign, send_notification, create_case
   - Enrolled: Number of entities currently being processed
   - Executions: Total times the automation has fired
   - HOW TO CREATE: Click "Create Automation" or ask me "create an automation that [trigger] then [action]"

7. INTERNAL WORKFLOWS PAGE (/InternalWorkflows):
   - Reusable workflow templates for data collection and multi-step processes
   - Categories: onboarding, compliance, financial_planning, data_collection, custom
   - Step types: form_input (collect data), entity_creation (create records), ai_guidance (AI suggestions), data_summary (review), data_mapping (map to client data), conditional_branch
   - HOW TO CREATE: Click "+ New Workflow" or ask me "create a workflow for [process]"

8. TASK CONFIGURATION / TASKFLOWS (/TaskConfiguration):
   - Define task sequences with dependencies
   - Creation modes: auto_create (next task auto-created when previous completes), create_all_upfront (all at once), manual
   - Sequential vs parallel execution
   - Steps have dependencies (depends_on_step_ids)
   - HOW TO CREATE: Ask me "create a taskflow for [process]"

9. FORMS PAGE (/Forms):
   - Client assessment questionnaires with scoring
   - Question types: scale (1-10), yes_no, single_choice, multiple_choice, text, long_text
   - 5 Financial Pillars: planning, investing, protection, tax, retirement
   - Scoring: Each question has max_score and weight; thresholds define excellent/good/needs_improvement
   - HOW TO CREATE: Ask me "create a form/assessment for [topic]" or use the Forms page

10. CALENDAR (/Calendar):
    - View and manage appointments
    - Meeting types: in_person, video_call, phone_call
    - Shows availability windows and booked slots
    - HOW TO SCHEDULE: Ask me "schedule a meeting with [client] on [date/time]"

11. COMMUNICATIONS (/Communications):
    - Client message history (emails, SMS, calls)
    - Types: email, sms, phone
    - Purpose tags: review_reminder, portfolio_update, check_in, recommendation_follow_up, etc.

12. DOCUMENTS & SIGNING (/DocumentSigning):
    - Document management and e-signature workflows
    - Upload, send for signature, track completion

13. NOTES:
    - Contextual notes attached to clients
    - Types: general, meeting, call, action_item, follow_up, recommendation
    - Visibility: "internal" (advisor only) or "external" (client can see in portal)
    - Priority: low, medium, high
    - Supports @mentions to notify other team members
    - HOW TO CREATE: Ask me "create a note for [client] about [content]"

14. HOUSEHOLDS (/Households):
    - Group related clients into family units
    - View combined financials and shared goals

15. LEADS (/Leads):
    - Pipeline management for prospects
    - Track conversion from lead to client

16. REPORTS (/Reports):
    - Analytics and reporting dashboard
    - Client performance, AUM, activity metrics

17. INTELLIGENCE CENTER (/IntelligenceCenter):
    - Train and customize Oli (me!) with your preferences
    - View personalization data and feedback history

18. USER MANAGEMENT (/UserManagement):
    - Manage team members, roles, permissions
    - Roles: super_admin, admin, advisor, support

19. ORGANIZATION SETTINGS:
    - Company branding, locations, integrations
    - Calendar settings, email templates

COMMON BADGE/STATUS MEANINGS:

Statuses across the app:
• "active" (green) = Currently running/engaged
• "draft" (gray/blue) = Created but not yet activated
• "paused" (amber/yellow) = Temporarily stopped
• "completed" (blue) = Successfully finished
• "cancelled" (gray) = Permanently stopped
• "archived" (light gray) = Hidden from active view
• "pending" = Awaiting action
• "in_progress" = Currently being worked on
• "waiting_on_client" = Blocked until client responds

Priority levels:
• "low" = Can wait, not time-sensitive
• "medium" = Normal priority, complete within standard timeframe
• "high" = Important, needs attention soon
• "urgent" = Critical, needs immediate action

Health Score colors:
• Green (70-100%) = Healthy financial position
• Yellow/Amber (40-69%) = Needs attention in some areas
• Red (0-39%) = Significant concerns, needs immediate planning

Campaign metrics:
• "Enrolled" = Number of clients currently in the campaign sequence
• "Assets" = Number of email/SMS templates linked to the campaign
• ">100" badge = More than 100 clients enrolled

Automation badges:
• Trigger type badges explain WHEN it fires (time_based = on schedule, event_based = when something happens)
• Target type badges explain WHAT it affects (client, task, goal, etc.)
• Actions count = How many things happen when triggered

═══════════════════════════════════════════════════
WHAT I CAN DO FOR YOU
═══════════════════════════════════════════════════

CREATE ENTITIES (just ask):
• "Create a task to follow up with John about his 401k" → I'll create it
• "Create a case for Sarah's onboarding" → I'll set it up
• "Add a note for Mike: discussed retirement timeline" → Done
• "Create a goal for Lisa to save $50k for a house by 2027" → Created
• "Schedule a video call with Tom next Tuesday at 2pm" → Booked
• "Create a campaign to re-engage inactive clients" → Full campaign with messages
• "Build a workflow for new client data collection" → Complete workflow template
• "Create a taskflow for annual review process" → Sequential task template

EXPLAIN FEATURES:
• "What does the health score mean?" → Full explanation
• "How do I create a campaign?" → Step-by-step guide
• "What's the difference between a task and a case?" → Clear comparison
• "What do the colored badges mean on automations?" → Badge explanations
• "How do taskflows work?" → Detailed walkthrough

ANALYZE DATA:
• "Which clients have declining health scores?" → Analysis with names
• "Show me my most active clients" → Data breakdown
• "How many tasks are overdue?" → Count and details
• "Segment my clients by engagement" → Smart segmentation

DRAFT COMMUNICATIONS:
• "Draft an email to John about his quarterly review" → Professional email
• "Write a check-in message for inactive clients" → Template ready

═══════════════════════════════════════════════════

TASK MANAGEMENT (Action Items & To-Dos):
- Creating TASKS for client follow-ups, document reviews, and action items
- Editing and updating existing tasks
- Setting task priorities, due dates, categories, and assignments
- Creating tasks linked to goals or financial pillars
- Distinguishing tasks from appointments (tasks = to-dos, appointments = scheduled meetings)

WORKFLOW & TASKFLOW CREATION:
- Building internal workflows for data collection and processing
- Designing taskflows with automatic task creation and dependencies
- Configuring sequential vs parallel task execution
- Setting up auto-create, upfront, or manual creation modes
- Assigning steps to roles and teams
- Creating reusable templates for common processes

OTHER CAPABILITIES:
- Creating/editing notes, goals, and appointments
- Building campaigns and forms
- Drafting client communications and emails
- Analyzing client data with predictive insights
- Segmenting clients for targeted outreach
- Managing financial data snapshots
- Answering data queries in natural language
- Providing insights and summaries
- Client relationship management
- Creating and managing cases with tasks and taskflows
- Building case templates for common client workflows
- Setting up taskflows with automatic task creation and dependencies
- Understanding sequential vs parallel taskflows
- Configuring case templates with embedded taskflows and workflows

CASE MANAGEMENT STRUCTURE:
- CASE = Project/matter for a client (e.g., "John's Onboarding", "Smith Annual Review 2025")
- TASKFLOW = Automated task sequence with dependencies (e.g., sequential onboarding steps)
- WORKFLOW = Data collection/processing workflow (e.g., collect client info, generate documents)
- TASK = Individual action item that may be part of a taskflow or standalone
- CASE TEMPLATE = Reusable template that can include tasks, taskflows, and workflows

TASKFLOW CAPABILITIES:
- Auto-create tasks based on dependencies (when previous task completes)
- Create all tasks upfront or create them as-needed (creation_mode)
- Sequential execution with step-by-step dependencies
- Team and role assignment per step
- Checklist templates for each task
- Multiple instances of same taskflow in one case

IMPORTANT DISTINCTIONS:
- TASK = Action item, to-do, follow-up, reminder (e.g., "Call John", "Review documents", "Send proposal")
- APPOINTMENT = Scheduled meeting with date/time (e.g., "Meeting with John on Monday at 2pm")
- When user asks to create a task, reminder, action item, follow-up, or to-do → create a TASK
- When user asks to schedule/book a meeting/appointment/call with specific time → create an APPOINTMENT

Current platform data:
- Clients: ${context.total_clients} total (${context.active_clients} active, ${context.lead_clients} leads)
- Client names (first 20): ${context.client_names?.join(', ') || 'None'}
- Tasks: ${context.total_tasks} total (${context.pending_actions} pending/in-progress)
- Goals: ${context.total_goals} total
- Appointments: ${context.total_appointments} total (${context.upcoming_appointments} scheduled)
- Cases: ${context.total_cases} total (${context.active_cases} in progress)
- Case templates: ${context.total_case_templates}
- Taskflows: ${context.total_taskflows} (${context.active_taskflows} active)
- Campaigns: ${context.total_campaigns} (${context.active_campaigns} active)
- Forms: ${context.total_forms}
- Notes: ${context.total_notes}
- Communications: ${context.total_communications}
- Workflows: ${context.total_workflows}

${(() => {
      const recent = messages.slice(-7, -1).filter(
        m => (m.role === 'user' || m.role === 'assistant') &&
             typeof m.content === 'string' &&
             m.content.length < 600 &&
             !['task_form', 'appointment_form', 'confirmation_pending', 'client_picker'].includes(m.content)
      );
      if (recent.length === 0) return '';
      return `\n═══════════════════════════════════════════════════\nCONVERSATION HISTORY (most recent messages)\n═══════════════════════════════════════════════════\n${recent.map(m => `${m.role === 'user' ? 'User' : 'Oli'}: ${m.content}`).join('\n')}\n`;
    })()}
User message: "${userInput}"

CRITICAL FORMATTING RULES - FOLLOW EXACTLY:
DO NOT USE any markdown symbols like **, *, ###, ##, #, ---, ___, etc.
Instead, use PLAIN TEXT with the following structure:

For section headers:
Write them on their own line ending with a colon
Example: "Campaign Details:" or "Recommended Actions:"

For bullet lists:
Start each item with a bullet point (•) character
Example:
• First item here
• Second item here
• Third item here

For numbered lists:
Use plain numbers with periods
Example:
1. First step
2. Second step
3. Third step

For emphasis:
Just write the text plainly - NO asterisks or symbols

Keep responses conversational, professional, and easy to scan. Focus on actionable insights.`;

    const response = await Promise.race([
      api.integrations.Core.InvokeLLM({
        prompt: prompt,
        add_context_from_internet: false,
        file_urls: fileUrls.length > 0 ? fileUrls : undefined,
        model: oliConfig.model || undefined,
        temperature: oliConfig.temperature ?? undefined,
        max_tokens: oliConfig.maxTokens || undefined,
      }),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 25000)
      )
    ]);

    const assistantMessage = {
      id: Date.now().toString(),
      role: "assistant",
      content: response,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, assistantMessage]);

    // Auto-log only genuinely new Q&A knowledge — skip transient data queries and duplicates
    const words = userInput.trim().split(/\s+/);
    const isSubstantial = typeof response === 'string' && response.length > 150 && words.length > 6;
    const isTransient = /how many|count|total|number of|list (my|all|the)|show me|what are my/i.test(userInput);
    if (isSubstantial && !isTransient) {
      const normalize = (s) => s.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
      const inputNorm = normalize(userInput);
      const inputWords = new Set(inputNorm.split(/\s+/).filter(w => w.length > 3));
      const alreadyKnown = (oliConfig.trainingData || []).some(item => {
        const itemWords = normalize(item.title || '').split(/\s+/).filter(w => w.length > 3);
        const overlap = itemWords.filter(w => inputWords.has(w)).length;
        return overlap >= 3 && overlap >= itemWords.length * 0.6;
      });
      if (!alreadyKnown) {
        api.entities.OliPersonalization.create({
          scope: "firm",
          title: userInput.slice(0, 100),
          content: response.slice(0, 600),
          category: "auto_learned",
          created_by: user?.email,
          created_by_name: user?.full_name,
          data: { auto_logged: true, logged_at: new Date().toISOString() }
        }).catch(() => {});
      }
    }
  };

  const { data: user } = useQuery({
    queryKey: ['current-user'],
    queryFn: () => api.auth.me(),
    enabled: isOpen,
  });

  // Load firm config, training data, and governance settings from IntelligenceCenter
  const oliConfig = useOliConfig(user?.role || user?.type);

  const { data: userPreferences } = useQuery({
    queryKey: ['user-preferences', user?.email],
    queryFn: async () => {
      const prefs = await api.entities.UserPreferences.filter({ user_email: user.email });
      return prefs[0] || null;
    },
    enabled: !!user && isOpen,
  });

  const quickActionConfigs = React.useMemo(() => {
    if (!userPreferences?.quick_actions) return [];
    return userPreferences.quick_actions
      .filter(action => action.is_enabled !== false && action.show_in_locations?.includes('oli_chat'))
      .sort((a, b) => a.order - b.order);
  }, [userPreferences]);

  const handleQuickAction = (action) => {
    if (action.action_type === 'navigate') {
      setInput(`Show me the ${action.navigation_target} section`);
    } else if (action.action_type === 'create') {
      setInput(`Create a new ${action.creation_target}`);
    } else {
      setInput(action.label);
    }
  };

  // Map icon names to components
  const getIconComponent = (iconName) => {
    const iconMap = {
      MessageSquare, Target, FileText, Calendar: CalendarIcon, CheckSquare, ClipboardList,
      Users: UsersIcon, Mail, Megaphone, Brain, ListChecks, GitBranch, Folder,
      BarChart3, TrendingUp
    };
    return iconMap[iconName] || Target;
  };

  const quickActions = quickActionConfigs.map(config => ({
    ...config,
    icon: getIconComponent(config.icon)
  }));

  if (!isOpen) return null;

  if (isMinimized) {
    return (
      <div className="fixed bottom-6 right-6 z-[49]">
        <Button
          onClick={onToggle}
          className="w-16 h-16 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-2xl"
        >
          <OliAiIcon className="w-8 h-8 text-white" />
        </Button>
      </div>
    );
  }

  return (
    <>
      <Dialog open={showWorkflowExecutor} onOpenChange={setShowWorkflowExecutor}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Workflow Execution</DialogTitle>
          </DialogHeader>
          {selectedWorkflow && (
            <WorkflowExecutor
              template={selectedWorkflow}
              onComplete={() => {
                setShowWorkflowExecutor(false);
                setSelectedWorkflow(null);
                queryClient.invalidateQueries();
                
                const completionMsg = {
                  id: Date.now().toString(),
                  role: "assistant",
                  content: `Great! You've successfully completed the "${selectedWorkflow.name}" workflow. All data has been saved.`,
                  timestamp: new Date().toISOString()
                };
                setMessages(prev => [...prev, completionMsg]);
              }}
              onCancel={() => {
                setShowWorkflowExecutor(false);
                setSelectedWorkflow(null);
                
                const cancelMsg = {
                  id: Date.now().toString(),
                  role: "assistant",
                  content: "Workflow cancelled. Let me know if you need anything else!",
                  timestamp: new Date().toISOString()
                };
                setMessages(prev => [...prev, cancelMsg]);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

    <div className="fixed inset-4 md:bottom-6 md:right-6 md:left-auto md:top-auto md:w-[450px] md:h-[85vh] md:max-h-[900px] h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] z-[49] flex flex-col bg-white rounded-2xl shadow-2xl border-2 border-slate-200">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <motion.div
              animate={{ 
                scale: [1, 1.15, 1],
                opacity: [0.7, 1, 0.7]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <OliAiIcon className="w-6 h-6 text-white" />
            </motion.div>
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Oli</h3>
            <p className="text-white/80 text-xs">Vestolio Intelligence Layer</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="text-white hover:bg-white/20"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      <ScrollArea ref={scrollRef} className="flex-1 p-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                message.role === 'user' 
                  ? 'bg-blue-500' 
                  : 'bg-gradient-to-r from-indigo-500 to-purple-500'
              }`}>
                {message.role === 'user' ? (
                  <User className="w-5 h-5 text-white" />
                ) : (
                  <OliAiIcon className="w-5 h-5 text-white" />
                )}
              </div>
              
              <div className={`flex-1 ${message.role === 'user' ? 'flex justify-end' : ''}`}>
                {message.content === "task_form" ? (
                  <TaskCreationForm
                    clients={clients}
                    prefill={message.prefillData || {}}
                    onSubmit={(taskData) => {
                      // Remove the form message
                      setMessages(prev => prev.filter(m => m.id !== message.id));
                      // Clean up client_name if "no client" was selected
                      const cleanedData = {
                        ...taskData,
                        client_name: taskData.client_name === '__none__' ? null : taskData.client_name,
                      };
                      // Match client to get client_id
                      if (cleanedData.client_name) {
                        const client = clients.find(c =>
                          c.full_name.toLowerCase() === cleanedData.client_name.toLowerCase()
                        );
                        if (client) {
                          cleanedData.client_id = client.id;
                        }
                      }
                      // Show confirmation card with the structured data
                      const confirmationMsg = {
                        id: Date.now().toString(),
                        role: "assistant",
                        content: "confirmation_pending",
                        timestamp: new Date().toISOString(),
                        actionType: 'create_task',
                        structuredData: cleanedData,
                        entityType: "Task",
                        successMessage: "task"
                      };
                      setMessages(prev => [...prev, confirmationMsg]);
                    }}
                    onCancel={() => {
                      setMessages(prev => prev.filter(m => m.id !== message.id));
                      setPendingAction(null);
                    }}
                  />
                ) : message.content === "appointment_form" ? (
                  <AppointmentCreationForm
                    clients={clients}
                    prefill={message.prefillData || {}}
                    onSubmit={(apptData) => {
                      setMessages(prev => prev.filter(m => m.id !== message.id));
                      // Resolve client_id if a client was selected
                      const finalData = { ...apptData };
                      if (finalData.client_name) {
                        const matched = clients.find(c =>
                          c.full_name.toLowerCase() === finalData.client_name.toLowerCase()
                        );
                        if (matched) {
                          finalData.client_id = matched.id;
                          finalData.client_name = matched.full_name;
                        }
                      }
                      const confirmationMsg = {
                        id: Date.now().toString(),
                        role: "assistant",
                        content: "confirmation_pending",
                        timestamp: new Date().toISOString(),
                        actionType: 'create_appointment',
                        structuredData: finalData,
                        entityType: "Appointment",
                        successMessage: "appointment"
                      };
                      setMessages(prev => [...prev, confirmationMsg]);
                    }}
                    onCancel={() => {
                      setMessages(prev => prev.filter(m => m.id !== message.id));
                      setPendingAction(null);
                    }}
                  />
                ) : message.content === "confirmation_pending" ? (
                   <Card className="border-2 border-amber-200 bg-amber-50">
                     <CardContent className="p-4">
                       <div className="space-y-3">
                         <div className="flex items-center gap-2">
                           <AlertCircle className="w-5 h-5 text-amber-600" />
                           <p className="text-sm font-semibold text-amber-900">Confirm Action</p>
                         </div>
                         <p className="text-xs text-amber-700">
                           Review the details below before creating the {message.entityType.toLowerCase()}:
                         </p>
                         <div className="bg-white rounded p-2 text-xs text-slate-700 space-y-1 max-h-40 overflow-y-auto">
                           {formatEntityDetails(message.entityType, null, message.structuredData).split('\n').map((line, idx) => (
                             <div key={idx}>{line}</div>
                           ))}
                         </div>
                         <div className="flex gap-2">
                           <Button
                             size="sm"
                             onClick={async () => {
                               const oldMessages = messages;
                               setMessages(prev => prev.filter(m => m.id !== message.id));

                               try {
                                 await performCreateAction(message.actionType, message.structuredData, message.entityType, message.successMessage);
                               } catch (error) {
                                 setMessages(oldMessages);
                               }
                             }}
                             className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                           >
                             Create
                           </Button>
                           <Button
                             size="sm"
                             variant="outline"
                             onClick={() => setMessages(prev => prev.filter(m => m.id !== message.id))}
                             className="flex-1"
                           >
                             Cancel
                           </Button>
                         </div>
                       </div>
                     </CardContent>
                   </Card>
                 ) : message.content === "action_completed" ? (
                   <Card className="border-2 border-emerald-200 bg-emerald-50">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                        <div>
                          <p className="text-sm font-semibold text-emerald-900">
                            {message.entityType} Created Successfully!
                          </p>
                          <p className="text-xs text-emerald-700 mt-1">
                            The {message.entityType.toLowerCase()} has been added to your system and logged in activity.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : message.content === "client_picker" ? (
                  <Card className="border-2 border-indigo-200 bg-indigo-50 max-w-[95%]">
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <UsersIcon className="w-4 h-4 text-indigo-600" />
                          <p className="text-sm font-semibold text-indigo-900">Select a client</p>
                        </div>
                        <p className="text-xs text-indigo-700">{message.promptText}</p>
                        <div className="flex flex-col gap-2">
                          {(message.candidates || []).map((c) => (
                            <button
                              key={c.id}
                              onClick={async () => {
                                setMessages(prev => prev.filter(m => m.id !== message.id));
                                setPendingAction(null);
                                const updated = { ...message.structuredData, client_id: c.id, client_name: c.full_name };
                                await performCreateAction(message.actionType, updated, message.entityType, message.successMessage);
                              }}
                              className="flex items-center gap-3 p-2.5 rounded-lg bg-white border border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50 text-left transition-colors"
                            >
                              <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-semibold text-indigo-700">
                                  {c.full_name?.charAt(0)?.toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-slate-800">{c.full_name}</p>
                                {c.email && <p className="text-xs text-slate-400">{c.email}</p>}
                              </div>
                            </button>
                          ))}
                        </div>
                        <button
                          onClick={() => {
                            setMessages(prev => prev.filter(m => m.id !== message.id));
                            setPendingAction(null);
                          }}
                          className="text-xs text-slate-400 hover:text-slate-600 underline"
                        >
                          Cancel
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ) : message.content === "confirmation_pending" || message.content === "action_completed" || message.content === "client_segments" ? (
                  <div className="max-w-[95%]">
                    <ClientSegments segments={message.segments} />
                  </div>
                ) : message.content === "questionnaire_preview" ? (
                  <div className="max-w-[95%]">
                    <QuestionnairePreview questionnaire={message.questionnaire} />
                  </div>
                ) : message.content === "campaign_preview" ? (
                  <div className="max-w-[95%]">
                    <CampaignPreview campaign={message.campaign} />
                  </div>
                ) : (
                  <div>
                   <div className={`rounded-2xl p-4 max-w-[85%] ${
                     message.role === 'user'
                       ? message.isFeedback 
                         ? 'bg-purple-500 text-white'
                         : 'bg-blue-500 text-white'
                       : message.isError
                       ? 'bg-red-50 border border-red-200 text-red-900'
                       : 'bg-white border border-slate-200 text-slate-900 shadow-sm'
                   }`}>
                     {message.role === 'user' ? (
                       <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                     ) : (
                       <div className="text-sm">
                         <FormattedMessage content={message.content} />
                       </div>
                     )}
                     <p className={`text-xs mt-2 ${
                       message.role === 'user' ? 'text-white/70' : 'text-slate-400'
                     }`}>
                       {formatTime(message.timestamp, timezone)}
                     </p>
                   </div>
                   {message.role === 'assistant' && !message.isError && !['action_completed', 'client_segments', 'questionnaire_preview', 'campaign_preview', 'client_picker', 'appointment_form'].includes(message.content) && (
                     <Button
                       variant="ghost"
                       size="sm"
                       onClick={() => {
                         setTrainingMode(true);
                         setFeedbackFor({
                           id: message.id,
                           content: message.content,
                           originalQuery: messages[messages.indexOf(message) - 1]?.content || ""
                         });
                         setInput("");
                       }}
                       className="mt-1 text-xs text-slate-500 hover:text-purple-600"
                     >
                       <Brain className="w-3 h-3 mr-1" />
                       Provide better response
                     </Button>
                   )}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center">
                <OliAiIcon className="w-5 h-5 text-white" />
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl p-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                  <span className="text-sm text-slate-600">Oli is creating...</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      <div className="p-4 border-t border-slate-200 bg-white rounded-b-2xl">
        {trainingMode && (
          <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-semibold text-purple-900">Training Mode</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTrainingMode(false);
                  setFeedbackFor(null);
                  setInput("");
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-purple-700">
              Provide the response you would have preferred. This will help Oli learn your preferences.
            </p>
          </div>
        )}
        <div className="flex gap-2 items-end">
          {messages.length === 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="icon"
                  className="flex-shrink-0 h-[44px] w-[44px]"
                  title="Quick Actions"
                >
                  <Zap className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {quickActions.map((action, idx) => {
                  const Icon = action.icon;
                  return (
                    <DropdownMenuItem
                      key={idx}
                      onClick={() => handleQuickAction(action)}
                      className="cursor-pointer"
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {action.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder="Ask Oli anything..."
            className="flex-1 min-h-[44px] max-h-[200px] resize-none overflow-y-auto"
            rows={1}
          />
          <Button
            onClick={handleSendMessage}
            disabled={!input.trim() || isLoading}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 flex-shrink-0 h-[44px] w-[44px]"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
    </>
  );
}